import { useEffect, useState, useCallback, useRef } from "react";
import StaffDashboard from "@/pages/StaffDashboard";
import ConnectAdmin from "@/components/mc/cn/ConnectAdmin";
import { brandFor, brandPath } from "@/lib/brand";
import FieldServiceAdmin from "@/components/mc/fs/FieldServiceAdmin";
import { TeamSettings } from "@/components/mc/TeamSettings";
import { MorrLink } from "@/components/mc/MorrLink";
import { MorrMenu } from "@/components/mc/MorrMenu";
import { BookingStats } from "@/components/mc/BookingStats";
import { LoyaltySetup } from "@/components/mc/LoyaltySetup";
import { CartAddons } from "@/components/mc/CartAddons";
import { GoogleSetup, GoogleSetupNudge } from "@/components/mc/GoogleSetup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOperator } from "@/context/OperatorContext";
import { opApi, apiError } from "@/lib/api";
import { Shell, StatCard, money, timeStr, dateStr } from "@/components/mc/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BarberAdmin from "@/components/mc/BarberAdmin";
import { CartTypeChooser } from "@/components/mc/CartTypeChooser";
import { AdminOnlyTag } from "@/components/mc/CartControls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Play, CheckCircle2, Clock, Trash2, Pencil, ArrowLeft, Calendar, BellRing, CalendarClock, Volume2, VolumeX, Printer, UserPlus, Lock, ExternalLink, Save, RefreshCw, Star, MapPin, ChevronUp, ChevronDown } from "lucide-react";
import { InstallSnippet } from "@/components/mc/InstallSnippet";
import { ConnectInstall } from "@/components/mc/ConnectInstall";
import { EmailProblems } from "@/components/mc/EmailProblems";
import { EmbedHealth } from "@/components/mc/EmbedHealth";
import { CartPreviewPanel } from "@/components/mc/CartPreviewPanel";
import { OnboardingModal } from "@/components/mc/OnboardingModal";
import { LoyaltyManager } from "@/components/mc/LoyaltyManager";
import { EventsManager, BookingsList } from "@/components/mc/EventsManager";
import { HoursEditor } from "@/components/mc/HoursEditor";
import { TimezoneSelect } from "@/components/mc/TimezoneSelect";
import { LocationWidgetEditor } from "@/components/mc/LocationWidgetEditor";
import { MenusPanel } from "@/components/mc/MenusPanel";
import { MarketingTab } from "@/components/mc/MarketingTab";
import { VariantEditor } from "@/components/mc/VariantEditor";
import { BrandPreview } from "@/components/mc/BrandPreview";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { CustomerLoyaltyCell } from "@/components/mc/CustomerLoyaltyCell";
import { CustomerEmailsCell } from "@/components/mc/CustomerEmailsCell";
import { RegisterTab } from "@/components/mc/RegisterTab";
import { EditOrderDialog } from "@/components/mc/EditOrderDialog";
import { StaffManager } from "@/components/mc/StaffManager";
import { PromoManager } from "@/components/mc/PromoManager";
import { ZReport } from "@/components/mc/ZReport";
import { HelpCenter } from "@/components/mc/HelpCenter";
import { AddCustomerDialog } from "@/components/mc/AddCustomerDialog";
import { CustomerDetailDialog } from "@/components/mc/CustomerDetailDialog";
import { ReportsTab } from "@/components/mc/ReportsTab";
import { ZReportTab } from "@/components/mc/ZReportTab";
import { CategoryPicker, CategoryTreeManager } from "@/components/mc/CategoryTreeManager";
import { ModifierAttach, ModifiersManager } from "@/components/mc/ModifiersManager";
import { playChime, primeAudio, requestNotifyPermission, notifyNewOrder } from "@/lib/orderAlerts";

const COLUMNS = [
  { key: "new", label: "New", tint: "border-t-primary" },
  { key: "preparing", label: "Preparing", tint: "border-t-[#FFBD2E]" },
  { key: "ready", label: "Ready", tint: "border-t-[hsl(var(--success))]" },
];

const LANES = {
  online: { label: "Online", cls: "bg-blue-100 text-blue-700" },
  in_person: { label: "In-Person", cls: "bg-amber-100 text-amber-800" },
  scheduled: { label: "Scheduled", cls: "bg-violet-100 text-violet-700" },
};
const LANE_FILTERS = [["all", "All lanes"], ["online", "Online"], ["in_person", "In-Person"], ["scheduled", "Scheduled"]];

function useTick(ms = 1000) {
  const [, setN] = useState(0);
  useEffect(() => { const id = setInterval(() => setN((n) => n + 1), ms); return () => clearInterval(id); }, [ms]);
}

function fmtCountdown(target) {
  const diff = Math.round((new Date(target).getTime() - Date.now()) / 1000);
  const abs = Math.abs(diff);
  const m = Math.floor(abs / 60), s = abs % 60;
  const str = `${m}:${String(s).padStart(2, "0")}`;
  return diff >= 0 ? { str, over: false } : { str: `+${str}`, over: true };
}

function OrderCard({ o, onAdvance, onPrint, onEdit, tz }) {
  const next = { new: "preparing", preparing: "ready", ready: "completed" }[o.status];
  const label = { new: "Accept", preparing: "Ready", ready: "Done" }[o.status];
  const Icon = { new: Play, preparing: CheckCircle2, ready: CheckCircle2 }[o.status];
  const showTimer = o.status === "preparing" && o.estimated_ready_at;
  const cd = showTimer ? fmtCountdown(o.estimated_ready_at) : null;
  const ageMin = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
  const agingBorder = o.status === "new" ? (ageMin >= 5 ? "border-l-4 border-l-red-500" : ageMin >= 2 ? "border-l-4 border-l-amber-400" : "") : "";

  return (
    <div className={`bg-card border border-border p-3 mb-3 ${agingBorder}`} data-testid={`order-card-${o.order_number}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-muted-foreground">#{o.order_number}</span>
        <span className="font-mono text-xs px-1.5 py-0.5 bg-secondary uppercase">{o.fulfillment === "pickup_later" ? "Later" : "Now"}{o.status === "new" && ageMin > 0 ? ` · ${ageMin}m` : ""}</span>
      </div>
      <div className="mt-1 font-medium text-sm">{o.customer_name}</div>
      {(o.lane || o.location_name) && (
        <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`order-lane-${o.order_number}`}>
          {o.lane && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${(LANES[o.lane] || {}).cls || "bg-secondary"}`}>{(LANES[o.lane] || {}).label || o.lane}</span>}
          {o.location_name && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-secondary inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{o.location_name}</span>}
        </div>
      )}
      <div className="mt-2 space-y-0.5">
        {o.items.map((it, i) => (
          <div key={i} className="flex justify-between text-xs text-muted-foreground">
            <span>{it.qty}× {it.name}</span><span className="font-mono">{money(it.price * it.qty)}</span>
          </div>
        ))}
      </div>
      {o.notes && <div className="mt-2 text-xs italic text-muted-foreground">"{o.notes}"</div>}
      {o.fulfillment === "pickup_later" && o.scheduled_at &&
        <div className="mt-2 flex items-center gap-1 text-xs text-primary"><Calendar className="h-3 w-3" /> {dateStr(o.scheduled_at, tz)}</div>}

      {showTimer && (
        <div className={`mt-2 flex items-center gap-2 border px-2 py-1.5 ${cd.over ? "border-accent text-accent" : "border-border"}`}>
          <Clock className="h-3.5 w-3.5 timer-pulse" />
          <span className="font-mono text-sm font-bold">{cd.str}</span>
          <span className="text-xs text-muted-foreground ml-auto">Ready ~{timeStr(o.estimated_ready_at, tz)}</span>
        </div>
      )}
      {o.status === "ready" && o.ready_at && (
        <div className="mt-2 text-xs text-[hsl(var(--success))] font-medium">Ready at {timeStr(o.ready_at, tz)}</div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="font-mono text-sm font-bold">{money(o.total)}{o.payment_status === "paid" ? " · paid" : ""}</span>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={() => onPrint(o)} data-testid={`print-${o.order_number}`} title="Print ticket">
            <Printer className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={() => onEdit(o)} data-testid={`edit-${o.order_number}`} title="Edit order">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {o.status !== "completed" && o.payment_status !== "paid" && (
            <Button size="sm" variant="outline" className="rounded-none h-7 gap-1 text-xs" onClick={() => onAdvance(o, "completed")} data-testid={`paid-${o.order_number}`} title="Mark paid & done">
              <CheckCircle2 className="h-3.5 w-3.5" /> Paid
            </Button>
          )}
          {next && (
            <Button size="sm" className="rounded-none h-7 gap-1 text-xs" onClick={() => onAdvance(o, next)} data-testid={`advance-${o.order_number}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const EMPTY_PROD = { name: "", description: "", price: "", group: "", category: "General", subcategory: "", category_id: null, modifier_group_ids: [], variant_options: [], image: "", images: [], available: true, cta_label: "", taxable: true, popular: false, channels: ["online", "pos"] };
const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) { return ""; } })();

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const imgSrc = (u) => (u && u.charAt(0) === "/" ? `${BACKEND}${u}` : u);

const BRAND_PRESETS = [
  { name: "Deep Blue", color: "#0033A0" }, { name: "Espresso", color: "#8C5A46" },
  { name: "Forest", color: "#1A7F5A" }, { name: "Teal", color: "#0F766E" },
  { name: "Crimson", color: "#B3261E" }, { name: "Tangerine", color: "#C2410C" },
  { name: "Plum", color: "#6D28D9" }, { name: "Rose", color: "#BE185D" },
  { name: "Slate", color: "#334155" }, { name: "Ink", color: "#111111" },
];

const LOC_TYPES = [["fixed", "Fixed shop"], ["mobile", "Mobile location"]];
const LOC_TYPE_LABEL = (t) => (t === "popup" || t === "mobile_truck" || t === "mobile") ? "Mobile location" : "Fixed shop";
const isMobileType = (t) => t === "popup" || t === "mobile_truck" || t === "mobile";
const WEEKDAYS = [["mon", "Mo"], ["tue", "Tu"], ["wed", "We"], ["thu", "Th"], ["fri", "Fr"], ["sat", "Sa"], ["sun", "Su"]];
const EMPTY_LOC = { name: "", type: "fixed", address: "", is_public: true, active: true, open: true, accepting_orders: true, starts_at: "", ends_at: "", hours: {}, holidays: [], phone: "", note: "", map_embed_url: "", show_in_widget: true, feature_top: false, menu_id: "" };

// Live open/closed + next window for a location — mirrors what customers see in the widget.
const _LDOW = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
function _lMin(t) { if (!t || String(t).indexOf(":") < 0) return null; const p = String(t).split(":"); return (+p[0]) * 60 + (+p[1] || 0); }
function _lFmt(t) { if (!t) return ""; const p = String(t).split(":"); let h = +p[0]; const m = +p[1] || 0; const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; return h + ":" + (m < 10 ? "0" : "") + m + " " + ap; }
function _lBlocks(rec) { rec = rec || {}; if (rec.blocks && rec.blocks.length) return rec.blocks.filter((b) => b && b.days && b.days.length); if (rec.days && rec.days.length) return [{ days: rec.days, start_time: rec.start_time, end_time: rec.end_time }]; return []; }
function _lRecurLabel(bls) {
  const lbl = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
  const order = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  return "Every " + bls.map((b) => {
    const days = (b.days || []).slice().sort((a, c) => order.indexOf(a) - order.indexOf(c)).map((d) => lbl[d] || d);
    return days.join(", ") + " · " + _lFmt(b.start_time) + (b.end_time ? " – " + _lFmt(b.end_time) : "");
  }).join("  |  ");
}
function _storeParts(tz) {
  const now = new Date();
  try {
    const p = {};
    new Intl.DateTimeFormat("en-CA", { timeZone: tz || undefined, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, weekday: "short" }).formatToParts(now).forEach((x) => { p[x.type] = x.value; });
    return { iso: `${p.year}-${p.month}-${p.day}`, cur: (parseInt(p.hour === "24" ? "0" : p.hour, 10)) * 60 + parseInt(p.minute, 10), dow: { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[p.weekday] };
  } catch (e) {
    return { iso: now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0"), cur: now.getHours() * 60 + now.getMinutes(), dow: now.getDay() };
  }
}
function locLiveStatus(l, tz) {
  // "Closed" wins over "Orders paused": a pause label only shows when the spot is otherwise open now.
  const a = _locSchedStatus(l, tz);
  if (a.tone === "open" && l.accepting_orders === false) return { tone: "closed", text: "Orders paused" };
  return a;
}
function _locSchedStatus(l, tz) {
  if (l.open === false) return { tone: "closed", text: "Closed for the day" };
  const { iso, cur, dow } = _storeParts(tz);
  if (l.closed_now_date && l.closed_now_date >= iso) return { tone: "closed", text: "Closed today (manual)" };
  if (l.open_now_date && l.open_now_date >= iso) return { tone: "open", text: "Open now (today)" };
  if (!isMobileType(l.type)) {
    if ((l.holidays || []).some((x) => x && x.date === iso)) return { tone: "closed", text: "Closed today" };
    const td = (l.hours || {})[_LDOW[dow]];
    if (!td || td.closed) return { tone: "closed", text: "Closed today" };
    const o = _lMin(td.open), c = _lMin(td.close);
    if (o != null && c != null && cur >= o && cur < c) return { tone: "open", text: "Open now · closes " + _lFmt(td.close) };
    if (o != null && cur < o) return { tone: "soon", text: "Opens " + _lFmt(td.open) };
    return { tone: "closed", text: "Closed" };
  }
  if ((l.holidays || []).some((x) => x && x.date === iso)) return { tone: "closed", text: "Closed today" };
  const rc = l.recurrence || {};
  if (rc.freq === "once") {
    if (rc.date !== iso) return rc.date > iso ? { tone: "soon", text: "On " + rc.date } : { tone: "closed", text: "Ended" };
    const so = _lMin(rc.start_time), eo = _lMin(rc.end_time);
    if (so != null && eo != null && cur >= so && cur < eo) return { tone: "open", text: "Open now · until " + _lFmt(rc.end_time) };
    if (so != null && cur < so) return { tone: "soon", text: "Opens " + _lFmt(rc.start_time) };
    return { tone: "closed", text: "Closed today" };
  }
  const bls = _lBlocks(l.recurrence);
  if (bls.length) {
    const rec = l.recurrence || {};
    if (rec.until && iso > rec.until) return { tone: "closed", text: "Schedule ended" };
    const dk = _LDOW[dow];
    for (const b of bls) {
      const s = _lMin(b.start_time), e = _lMin(b.end_time);
      if ((b.days || []).indexOf(dk) >= 0 && s != null && e != null && cur >= s && cur < e)
        return { tone: "open", text: "Open now · until " + _lFmt(b.end_time) };
    }
    return { tone: "soon", text: _lRecurLabel(bls) };
  }
  if (l.starts_at) {
    const s = new Date(l.starts_at).getTime(), e = l.ends_at ? new Date(l.ends_at).getTime() : s + 3 * 3600 * 1000, n = Date.now();
    if (n >= s && n < e) return { tone: "open", text: "Live now · until " + new Date(e).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) };
    if (n < s) return { tone: "soon", text: "Next " + new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) };
    return { tone: "closed", text: "Ended" };
  }
  return { tone: "soon", text: "Schedule varies" };
}
const LOC_TONE = { open: "bg-green-100 text-green-700", soon: "bg-amber-100 text-amber-700", closed: "bg-muted text-muted-foreground" };
const LOC_DOT = { open: "bg-green-500", soon: "bg-amber-500", closed: "bg-muted-foreground/50" };
// Current tri-state operating mode of a location/primary doc (store-local): auto | open | closed.
function _modeOf(doc, tz) {
  if (!doc) return "auto";
  const { iso } = _storeParts(tz);
  if (doc.closed_now_date && doc.closed_now_date >= iso) return "closed";
  if (doc.open_now_date && doc.open_now_date >= iso) return "open";
  return "auto";
}
// Tri-state operating-mode segmented control (Auto | Open now | Closed today).
function ModeToggle({ mode, onChange, size = "md", testidPrefix = "mode" }) {
  const opts = [
    { key: "auto", label: "Follow store hours", on: "!bg-slate-700 !text-white border-slate-700", off: "bg-background text-muted-foreground border-input hover:border-slate-400" },
    { key: "open", label: "Open now", on: "!bg-emerald-600 !text-white border-emerald-600", off: "bg-background text-emerald-700 border-input hover:border-emerald-400" },
    { key: "closed", label: "Closed", on: "!bg-red-600 !text-white border-red-600", off: "bg-background text-red-700 border-input hover:border-red-400" },
  ];
  const pad = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs";
  return (
    <div className="inline-flex rounded-none overflow-hidden border border-input divide-x divide-input" data-testid={`${testidPrefix}-toggle`}>
      {opts.map((o) => (
        <button key={o.key} type="button" onClick={() => mode !== o.key && onChange(o.key)}
          className={`${pad} font-semibold transition-colors ${mode === o.key ? o.on : o.off}`}
          data-testid={`${testidPrefix}-${o.key}`} aria-pressed={mode === o.key}>{o.label}</button>
      ))}
    </div>
  );
}


// A mobile location whose schedule is permanently over (hide from the operational Board;
// still shown in the Locations tab under "Ended" for editing/deleting).
function _mobileEnded(l, tz) {
  if (!isMobileType(l.type)) return false;
  const { iso } = _storeParts(tz);
  const rc = l.recurrence || {};
  if (rc.freq === "once") return !!rc.date && rc.date < iso;
  if (rc.until) return rc.until < iso;
  if (l.ends_at && !(rc.blocks && rc.blocks.length) && !(rc.days && rc.days.length)) {
    try {
      const d = new Date(l.ends_at);
      const eiso = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
      return eiso < iso;
    } catch (e) { /* ignore */ }
  }
  return false;
}

function LocationsTab({ entitlements, locations, reload, menus = [], products = [], modGroups = [], primaryMenuId, onGoToSettings, tz }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_LOC);
  const locked = !entitlements.multi_location;
  const rec = form.recurrence || null;
  const blocks = (rec && rec.blocks) || (rec && rec.days ? [{ days: rec.days, start_time: rec.start_time, end_time: rec.end_time }] : []);
  const recOn = !!(rec && blocks.length);
  const patchRec = (patch) => setForm({ ...form, recurrence: { freq: "weekly", ...(form.recurrence || {}), ...patch } });
  const setBlocks = (bl) => patchRec({ blocks: bl });
  const enableRecur = (on) => setForm({ ...form, recurrence: on ? { freq: "weekly", blocks: blocks.length ? blocks : [{ days: ["sat"], start_time: "08:00", end_time: "11:00" }], until: (form.recurrence && form.recurrence.until) || "" } : null });
  const addBlock = () => setBlocks([...blocks, { days: ["sat"], start_time: "08:00", end_time: "11:00" }]);
  const updBlock = (i, patch) => setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const rmBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const toggleBlockDay = (i, k) => { const days = blocks[i].days || []; updBlock(i, { days: days.includes(k) ? days.filter((d) => d !== k) : [...days, k] }); };
  const closures = form.holidays || [];
  const setClosures = (cs) => setForm({ ...form, holidays: cs });
  // Mobile schedule mode: "once" (single date) or "weekly" (repeats).
  const schedMode = (form.recurrence && form.recurrence.freq === "once") ? "once"
    : (form.recurrence && (form.recurrence.blocks || form.recurrence.days)) ? "weekly" : "once";
  const onceDate = (rec && rec.freq === "once" && rec.date) || (form.starts_at ? String(form.starts_at).slice(0, 10) : "");
  const onceStart = (rec && rec.freq === "once" && rec.start_time) || (form.starts_at ? String(form.starts_at).slice(11, 16) : "");
  const onceEnd = (rec && rec.freq === "once" && rec.end_time) || (form.ends_at ? String(form.ends_at).slice(11, 16) : "");
  const patchOnce = (patch) => setForm({ ...form, recurrence: { freq: "once", date: onceDate, start_time: onceStart, end_time: onceEnd, ...(rec && rec.freq === "once" ? rec : {}), ...patch }, starts_at: "", ends_at: "" });
  const setSchedMode = (m) => {
    if (m === "once") setForm({ ...form, recurrence: { freq: "once", date: onceDate || "", start_time: onceStart || "09:00", end_time: onceEnd || "17:00" }, starts_at: "", ends_at: "" });
    else setForm({ ...form, recurrence: { freq: "weekly", blocks: blocks.length ? blocks : [{ days: ["sat"], start_time: "08:00", end_time: "11:00" }], until: (rec && rec.until) || "" }, starts_at: "", ends_at: "" });
  };
  const hasSchedule = schedMode === "once" ? !!onceDate : blocks.some((b) => (b.days || []).length);

  const shops = locations.filter((l) => !isMobileType(l.type));
  const mobiles = locations.filter((l) => isMobileType(l.type));
  const mobilesLive = mobiles.filter((l) => !_mobileEnded(l, tz));
  const mobilesEnded = mobiles.filter((l) => _mobileEnded(l, tz));
  const locRow = (l) => (
    <div key={l.id} className="flex items-center justify-between border border-border p-3" data-testid={`location-row-${l.id}`}>
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary" />
        <div>
          <div className="font-medium text-sm flex items-center gap-2">
            <span>{l.name} <span className="text-[10px] font-mono uppercase text-muted-foreground">· {LOC_TYPE_LABEL(l.type)}</span></span>
            {l.active !== false && l.is_public !== false && (() => { const st = locLiveStatus(l); return (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${LOC_TONE[st.tone]}`} data-testid={`location-status-${l.id}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${LOC_DOT[st.tone]}`} />{st.text}
              </span>
            ); })()}
          </div>
          <div className="text-xs text-muted-foreground">{l.address || "No address"} {l.is_public === false ? "· hidden" : ""} {l.active === false ? "· inactive" : ""} {l.show_in_widget === false ? "· not in widget" : ""}</div>
          {(() => {
            const noSched = isMobileType(l.type) && !((l.recurrence && (l.recurrence.freq === "once" ? l.recurrence.date : _lBlocks(l.recurrence).length)) || l.starts_at);
            return noSched ? <div className="text-[11px] font-semibold text-amber-600 mt-0.5" data-testid={`location-noschedule-${l.id}`}>⚠ No schedule — won’t show to customers</div> : null;
          })()}
          {isMobileType(l.type) && l.starts_at && !(l.recurrence && (l.recurrence.blocks || l.recurrence.days)) && (
            <div className="text-xs text-primary mt-0.5">{new Date(l.starts_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{l.ends_at ? ` – ${new Date(l.ends_at).toLocaleString([], { hour: "numeric", minute: "2-digit" })}` : ""}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" className="rounded-none" onClick={() => edit(l)} data-testid={`edit-location-${l.id}`}><Pencil className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => del(l.id)} data-testid={`del-location-${l.id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>
    </div>
  );

  const save = async () => {
    try {
      const payload = form.type === "mobile" ? { ...form, starts_at: "", ends_at: "" } : form;
      if (editId) await opApi.put(`/tenant/locations/${editId}`, payload);
      else await opApi.post("/tenant/locations", payload);
      toast.success("Location saved"); setOpen(false); setEditId(null); setForm(EMPTY_LOC); reload();
    } catch (e) {
      if (e.response?.status === 403) toast.error("Upgrade to Business to add locations.");
      else toast.error(apiError(e.response?.data?.detail));
    }
  };
  const del = async (id) => { try { await opApi.delete(`/tenant/locations/${id}`); toast.success("Location removed"); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const edit = (l) => { setEditId(l.id); const _rec = (l.recurrence && l.recurrence.days && !l.recurrence.blocks && l.recurrence.freq !== "once") ? { freq: "weekly", blocks: [{ days: l.recurrence.days, start_time: l.recurrence.start_time, end_time: l.recurrence.end_time }], until: l.recurrence.until || "" } : (l.recurrence || null); setForm({ name: l.name || "", type: isMobileType(l.type) ? "mobile" : "fixed", address: l.address || "", is_public: l.is_public !== false, active: l.active !== false, open: l.open !== false, accepting_orders: l.accepting_orders !== false, starts_at: l.starts_at || "", ends_at: l.ends_at || "", hours: l.hours || {}, holidays: l.holidays || [], recurrence: _rec, phone: l.phone || "", note: l.note || "", map_embed_url: l.map_embed_url || "", show_in_widget: l.show_in_widget !== false, feature_top: l.feature_top === true, menu_id: l.menu_id || "" }); setOpen(true); };

  if (locked) {
    return (
      <div className="border border-dashed border-primary/40 bg-primary/5 p-10 text-center max-w-xl mx-auto" data-testid="locations-upgrade-cta">
        <MapPin className="h-8 w-8 mx-auto text-primary mb-3" />
        <h2 className="font-display font-bold text-xl">Run multiple locations, trucks &amp; pop-ups</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Tag every order by location, give each spot its own board lane, and publish public pop-ups like "Find us Sat 10–2 @ Home Depot". Available on the <b>Business</b> plan.</p>
        <a href="#" title="Contact us to upgrade" onClick={(e) => e.preventDefault()}>
          <Button className="rounded-none mt-4" data-testid="locations-upgrade-btn">Upgrade to Business</Button>
        </a>
        <p className="text-[11px] text-muted-foreground mt-2">Contact us to upgrade.</p>
      </div>
    );
  }
  return (
    <div className="max-w-2xl">
      <MenusPanel menus={menus} products={products} modGroups={modGroups} primaryMenuId={primaryMenuId} reload={reload} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-lg">Additional locations</h2>
          <p className="text-xs text-muted-foreground max-w-md">Add extra fixed shops and mobile locations here (a Business upgrade). Orders are tagged to a location so each spot gets its own board lane.</p>
          <p className="text-xs text-muted-foreground mt-1">Your first/primary location is managed in{" "}
            <button type="button" onClick={onGoToSettings} className="text-primary font-medium underline underline-offset-2 hover:opacity-80" data-testid="primary-location-settings-link">Settings → Location &amp; Contact Widget</button>.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(EMPTY_LOC); } }}>
          <DialogTrigger asChild><Button className="rounded-none gap-1" data-testid="add-location-btn"><Plus className="h-4 w-4" /> Add location</Button></DialogTrigger>
          <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} location</DialogTitle>
              <DialogDescription>Name it, add an address, and set its own hours — everything flows into your public location widget.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="location-name-input" /></div>
              <div><Label>Type</Label>
                <select className="w-full h-9 rounded-none border border-input bg-background px-2 text-sm mt-1" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="location-type-select">
                  {LOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><Label>Address (optional)</Label><Input className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="location-address-input" /></div>
              {form.type === "mobile" && (
                <div className="border border-dashed border-primary/40 p-3 space-y-3" data-testid="popup-schedule">
                  <Label className="font-normal text-sm">Schedule</Label>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button type="button" onClick={() => setSchedMode("once")} className={`h-9 text-xs font-semibold border rounded-none transition-colors ${schedMode === "once" ? "border-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`} data-testid="sched-mode-once">One-time date</button>
                    <button type="button" onClick={() => setSchedMode("weekly")} className={`h-9 text-xs font-semibold border rounded-none transition-colors ${schedMode === "weekly" ? "border-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`} data-testid="sched-mode-weekly">Repeats weekly</button>
                  </div>
                  {schedMode === "once" ? (
                    <div className="space-y-2" data-testid="sched-once">
                      <div><Label className="text-[10px] uppercase text-muted-foreground">Date</Label><Input type="date" className="rounded-none mt-1" value={onceDate} onChange={(e) => patchOnce({ date: e.target.value })} data-testid="loc-once-date" /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-[10px] uppercase text-muted-foreground">Start time</Label><Input type="time" className="rounded-none mt-1" value={onceStart} onChange={(e) => patchOnce({ start_time: e.target.value })} data-testid="loc-once-start" /></div>
                        <div><Label className="text-[10px] uppercase text-muted-foreground">End time</Label><Input type="time" className="rounded-none mt-1" value={onceEnd} onChange={(e) => patchOnce({ end_time: e.target.value })} data-testid="loc-once-end" /></div>
                      </div>
                      <p className="text-[11px] text-muted-foreground">A single event. It shows to customers before/while live and disappears after this date.</p>
                    </div>
                  ) : (
                    <>
                      {blocks.map((b, i) => (
                        <div key={i} className="border border-border p-2.5 space-y-2 bg-muted/30" data-testid={`loc-recur-block-${i}`}>
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] uppercase text-muted-foreground">Repeat on</Label>
                            {blocks.length > 1 && <button type="button" onClick={() => rmBlock(i)} className="text-xs text-destructive hover:underline" data-testid={`loc-recur-block-remove-${i}`}>Remove</button>}
                          </div>
                          <div className="flex gap-1">
                            {WEEKDAYS.map(([k, l]) => (
                              <button key={k} type="button" onClick={() => toggleBlockDay(i, k)} className={`h-8 w-9 text-xs border rounded-none transition-colors ${(b.days || []).includes(k) ? "border-primary bg-primary/10 font-semibold" : "border-border hover:border-primary/50"}`} data-testid={`loc-recur-day-${i}-${k}`}>{l}</button>
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-[10px] uppercase text-muted-foreground">Start time</Label><Input type="time" className="rounded-none mt-1" value={b.start_time || ""} onChange={(e) => updBlock(i, { start_time: e.target.value })} data-testid={`loc-recur-start-${i}`} /></div>
                            <div><Label className="text-[10px] uppercase text-muted-foreground">End time</Label><Input type="time" className="rounded-none mt-1" value={b.end_time || ""} onChange={(e) => updBlock(i, { end_time: e.target.value })} data-testid={`loc-recur-end-${i}`} /></div>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={addBlock} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="loc-recur-add-block"><Plus className="h-3 w-3" /> Add another time block</button>
                      <div><Label className="text-[10px] uppercase text-muted-foreground">Until (optional)</Label><Input type="date" className="rounded-none mt-1" value={(form.recurrence && form.recurrence.until) || ""} onChange={(e) => patchRec({ until: e.target.value })} data-testid="loc-recur-until" /></div>
                      <div className="border-t border-border pt-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[10px] uppercase text-muted-foreground">Special closures</Label>
                          <button type="button" onClick={() => setClosures([...closures, { date: "", note: "" }])} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="loc-closure-add"><Plus className="h-3 w-3" /> Add closure</button>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Holiday or one-off dates when this spot is closed even though it normally repeats.</p>
                        {closures.map((c, i) => (
                          <div key={i} className="flex items-center gap-2 mt-1.5" data-testid={`loc-closure-${i}`}>
                            <Input type="date" className="rounded-none h-8 w-40" value={c.date || ""} onChange={(e) => setClosures(closures.map((x, idx) => (idx === i ? { ...x, date: e.target.value } : x)))} data-testid={`loc-closure-date-${i}`} />
                            <Input className="rounded-none h-8 flex-1" placeholder="Reason (optional)" value={c.note || ""} onChange={(e) => setClosures(closures.map((x, idx) => (idx === i ? { ...x, note: e.target.value } : x)))} data-testid={`loc-closure-note-${i}`} />
                            <button type="button" onClick={() => setClosures(closures.filter((_, idx) => idx !== i))} className="text-destructive" data-testid={`loc-closure-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {!hasSchedule && (
                    <p className="text-[11px] font-semibold text-amber-600 flex items-center gap-1" data-testid="sched-warning">⚠ No schedule set — this location won’t show to customers or take orders until you set a {schedMode === "once" ? "date" : "day & time"}.</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Phone (optional)</Label><Input className="rounded-none mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(415) 555-2671" data-testid="location-phone-input" /></div>
                <div className="flex items-end pb-1"><div className="flex items-center justify-between w-full"><Label className="font-normal text-xs">Show in location widget</Label><Switch checked={form.show_in_widget} onCheckedChange={(v) => setForm({ ...form, show_in_widget: v })} data-testid="location-widget-toggle" /></div></div>
              </div>
              <div><Label>Note / directions (optional)</Label><Textarea className="rounded-none mt-1" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Corner of 5th & Main, look for the blue awning." data-testid="location-note-input" /></div>
              {form.type === "fixed" && (
                <div className="border-t border-border pt-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Hours for this location</Label>
                  <div className="mt-2"><HoursEditor hours={form.hours} holidays={form.holidays} onChange={(hrs, hol) => setForm({ ...form, hours: hrs, holidays: hol })} /></div>
                </div>
              )}
              <div className="flex items-center justify-between"><Label className="font-normal">Show to customers (public)</Label><Switch checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} data-testid="location-public-toggle" /></div>
              <div className="flex items-center justify-between"><Label className="font-normal">Active</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="location-active-toggle" /></div>
              <div className="flex items-center justify-between"><div><Label className="font-normal">Feature at top of menu</Label><p className="text-xs text-muted-foreground mt-0.5">Show an ad-style card at the top of your menu (mobile locations appear only while live or upcoming).</p></div><Switch checked={form.feature_top} onCheckedChange={(v) => setForm({ ...form, feature_top: v })} data-testid="location-feature-top-toggle" /></div>
              <div className="border-t border-border pt-3">
                <Label>Menu for this location</Label>
                <select className="w-full h-9 rounded-none border border-input bg-background px-2 text-sm mt-1" value={form.menu_id || ""} onChange={(e) => setForm({ ...form, menu_id: e.target.value })} data-testid="location-menu-select">
                  <option value="">Full catalog (all products)</option>
                  {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Customers picking this spot see only this menu, at its prices.</p>
              </div>
            </div>
            <DialogFooter><Button className="rounded-none" onClick={save} disabled={!form.name.trim()} data-testid="save-location-btn">Save location</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-5">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Shops <span className="font-normal normal-case">· additional fixed storefronts</span></div>
          <div className="space-y-2" data-testid="shops-section">
            {shops.map(locRow)}
            {!shops.length && <div className="text-sm text-muted-foreground italic py-4 text-center border border-dashed border-border">No additional shops yet — add a permanent second storefront.</div>}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Mobile locations <span className="font-normal normal-case">· popups, trucks & one-time stops</span></div>
          <div className="space-y-2" data-testid="mobile-section">
            {mobilesLive.map(locRow)}
            {!mobilesLive.length && <div className="text-sm text-muted-foreground italic py-4 text-center border border-dashed border-border">No active mobile locations — add a popup or a mobile stop.</div>}
          </div>
          {mobilesEnded.length > 0 && (
            <details className="mt-4" data-testid="ended-section">
              <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-muted-foreground select-none">Ended <span className="font-normal normal-case">· past one-time stops & finished schedules ({mobilesEnded.length}) — hidden from the Board</span></summary>
              <div className="space-y-2 mt-2 opacity-70">
                {mobilesEnded.map(locRow)}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}


function nextOpenLabel(t) {
  const hours = (t && t.hours) || {};
  const order = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const names = { sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday" };
  const fmt = (v) => { if (!v) return ""; const p = String(v).split(":"); let h = +p[0]; const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; return h + ":" + (p[1] || "00") + " " + ap; };
  const now = new Date();
  for (let i = 1; i <= 7; i++) {
    const d = new Date(now); d.setDate(now.getDate() + i);
    const hrs = hours[order[d.getDay()]];
    if (hrs && !hrs.closed && hrs.open) return "opens " + (i === 1 ? "tomorrow" : names[order[d.getDay()]]) + " " + fmt(hrs.open);
  }
  return "reopens next business day";
}

export default function DashboardGate() {
  const { operator } = useOperator();
  if (operator && operator.team_role === "staff") return <StaffDashboard />;
  return <TenantDashboard />;
}

function TenantDashboard() {
  const { operator, logout, stopImpersonate, refresh } = useOperator();
  const cartControls = operator?.cart_controls || "hidden";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusView = ["register", "board"].includes(searchParams.get("focus")) ? searchParams.get("focus") : null;
  useTick(1000);

  const [ov, setOv] = useState(null);
  const [storeDialog, setStoreDialog] = useState(null);
  const [pauseMode, setPauseMode] = useState("walkups");
  const [orders, setOrders] = useState([]);
  const [completed, setCompleted] = useState({ count: 0, total: 0, collected: 0, orders: [], date: "" });
  const [completedFrom, setCompletedFrom] = useState("");
  const [completedTo, setCompletedTo] = useState("");
  const [completedSearch, setCompletedSearch] = useState("");
  const [reportSub, setReportSub] = useState("sales");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loyalty, setLoyalty] = useState([]);
  const [settings, setSettings] = useState(null);
  const [prodForm, setProdForm] = useState(EMPTY_PROD);
  const [categories, setCategories] = useState([]);
  const [catNodes, setCatNodes] = useState([]);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [modGroups, setModGroups] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [modMgrOpen, setModMgrOpen] = useState(false);
  const [customCat, setCustomCat] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const fileRef = useRef();
  const logoRef = useRef();
  const [logoUploading, setLogoUploading] = useState(false);
  const [settingsSections, setSettingsSections] = useState([]);
  const [editId, setEditId] = useState(null);
  const [prodOpen, setProdOpen] = useState(false);
  const pollRef = useRef();
  const seenRef = useRef(new Set());
  const initedRef = useRef(false);
  const seenBookingsRef = useRef(new Set());
  const bkInitedRef = useRef(false);
  const mutedRef = useRef(false);
  const [muted, setMuted] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [newBookingCount, setNewBookingCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [entitlements, setEntitlements] = useState({ multi_location: false, features: [] });
  const [locations, setLocations] = useState([]);
  const [menus, setMenus] = useState([]);
  const [laneFilter, setLaneFilter] = useState("all");
  const [locFilter, setLocFilter] = useState("");
  const [locPanelOpen, setLocPanelOpen] = useState(false);
  const [custSearch, setCustSearch] = useState("");
  const [prodSearch, setProdSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [prodCat, setProdCat] = useState("all");
  const [loyStatuses, setLoyStatuses] = useState({});
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [detailCust, setDetailCust] = useState(null);
  const [shift, setShiftRaw] = useState(() => {
    try { const s = sessionStorage.getItem("mc_shift"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [elevated, setElevated] = useState(false);
  const setShift = useCallback((emp) => {
    setShiftRaw(emp);
    setElevated(false);
    try { emp ? sessionStorage.setItem("mc_shift", JSON.stringify(emp)) : sessionStorage.removeItem("mc_shift"); } catch {}
  }, []);
  const [activeTab, setActiveTab] = useState(null);
  const [cartType, setCartType] = useState("food");
  const [vConfig, setVConfig] = useState(null);
  const [lockPin, setLockPin] = useState("");
  const [lockTarget, setLockTarget] = useState(null);  // tab the cashier is trying to open

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await opApi.get("/tenant/orders");
      if (initedRef.current) {
        const fresh = data.filter((o) => o.status === "new" && !seenRef.current.has(o.id));
        if (fresh.length) {
          if (!mutedRef.current) playChime();
          fresh.forEach((o) => { notifyNewOrder(o); toast.success(`New order #${o.order_number} received`); });
          setNewCount((c) => c + fresh.length);
        }
      }
      data.forEach((o) => seenRef.current.add(o.id));
      initedRef.current = true;
      setOrders(data);
      try { const { data: st } = await opApi.get("/tenant/board-stats"); setStats(st); } catch (e) {}
      try { const { data: cs } = await opApi.get("/tenant/customers"); setCustomers(cs); } catch (e) {}
      try {
        const { data: bk } = await opApi.get("/tenant/bookings");
        if (bkInitedRef.current) {
          const freshBk = bk.filter((b) => b.status === "requested" && !seenBookingsRef.current.has(b.id));
          if (freshBk.length) {
            if (!mutedRef.current) playChime();
            freshBk.forEach((b) => toast.success(`New event request from ${(b.first_name || "") + " " + (b.last_name || "")}`.trim(), { description: `${b.date} · ${b.package_name || "Event"}` }));
            setNewBookingCount((c) => c + freshBk.length);
          }
        }
        bk.forEach((b) => seenBookingsRef.current.add(b.id));
        bkInitedRef.current = true;
        setBookings(bk);
      } catch (e) {}
    } catch (e) {}
  }, []);

  const loadCompleted = useCallback(async (from, to) => {
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await opApi.get("/tenant/orders/completed", { params });
      setCompleted(data);
    } catch (e) {}
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [a, b, c, d, e, f] = await Promise.all([
        opApi.get("/tenant/overview"), opApi.get("/tenant/products"), opApi.get("/tenant/customers"),
        opApi.get("/tenant/loyalty"), opApi.get("/tenant/settings"), opApi.get("/tenant/categories"),
      ]);
      setOv(a.data); setProducts(b.data); setCustomers(c.data); setLoyalty(d.data); setSettings(e.data);
      setCategories(f.data || []);
      try { const { data: cn } = await opApi.get("/tenant/product-categories"); setCatNodes(cn || []); } catch (er) {}
      try { const { data: mg } = await opApi.get("/tenant/modifier-groups"); setModGroups(mg || []); } catch (er) {}
      try { const { data: ls } = await opApi.get("/tenant/loyalty/statuses"); setLoyStatuses(ls || {}); } catch (er) {}
      try { const { data: ent } = await opApi.get("/tenant/entitlements"); setEntitlements(ent); } catch (er) {}
      try { const { data: locs } = await opApi.get("/tenant/locations"); setLocations(locs); } catch (er) {}
      try { const { data: mn } = await opApi.get("/tenant/menus"); setMenus(mn || []); } catch (er) { setMenus([]); }
      try { const { data: ct } = await opApi.get("/tenant/cart-type"); setCartType(ct.cart_type || "food"); setVConfig(ct.config || null); } catch (er) {}
    } catch (err) {}
  }, []);

  useEffect(() => {
    if (operator === false) { navigate(brandPath("/login")); return; }
    if (operator && !operator.tenant_id) return;
    if (operator && operator.tenant_id) { loadAll(); loadOrders(); }
  }, [operator, navigate, loadAll, loadOrders]);

  useEffect(() => {
    if (operator && operator.tenant_id) {
      pollRef.current = setInterval(loadOrders, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [operator, loadOrders]);

  useEffect(() => {
    if (operator && operator.tenant_id && (activeTab === "completed" || (activeTab === "reporting" && reportSub === "completed"))) loadCompleted(completedFrom, completedTo);
  }, [operator, activeTab, reportSub, completedFrom, completedTo, loadCompleted]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      const key = e.key.toLowerCase();
      const oldest = (status) => orders.filter((o) => o.status === status).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
      if (key === "a") { const o = oldest("new"); if (o) advance(o, "preparing"); }
      else if (key === "r") { const o = oldest("preparing"); if (o) advance(o, "ready"); }
      else if (key === "c") { const o = oldest("ready"); if (o) advance(o, "completed"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const obKey = `mc_onboard_dismissed_${operator?.tenant?.id || operator?.tenant_id}`;
  const [obDismissed, setObDismissed] = useState(() => sessionStorage.getItem(obKey) === "1");
  const dismissOnboarding = () => { sessionStorage.setItem(obKey, "1"); setObDismissed(true); };
  const reopenOnboarding = () => { sessionStorage.removeItem(obKey); setObDismissed(false); };
  useEffect(() => { setObDismissed(sessionStorage.getItem(obKey) === "1"); }, [obKey]);

  if (!operator || operator === null) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (operator && !operator.tenant_id) {
    return (
      <Shell badge={operator.role} onLogout={logout}>
        <div className="max-w-md mt-16 mx-auto text-center">
          <h2 className="font-display font-bold text-xl">No store assigned</h2>
          <p className="mt-2 text-sm text-muted-foreground">Your MorrAccess account has access but isn't linked to a store yet. Ask the platform owner to assign you a tenant.</p>
        </div>
      </Shell>
    );
  }

  const modes = operator.tenant?.modes || ["ordering"];
  const hasOrdering = modes.includes("ordering");
  const hasBooking = modes.includes("booking");
  const ORDERING_ONLY = ["board", "completed", "register", "products", "locations"];
  // Foundation-level: the vertical config (from the backend registry) decides which tabs this cart
  // type exposes and how they're labelled. Adding a vertical = a config entry, never per-vertical
  // hiding code here. The mode (ordering/booking) + role filters still apply generically on top.
  const inVertical = (t) => !vConfig?.tabs || vConfig.tabs.includes(t);
  // Widgets follow the Morr Access plan: a widget switched off hides its tabs (data is kept).
  const widgets = operator.tenant?.widgets || {};
  const WIDGET_TABS = { menu: ORDERING_ONLY, events: ["events", "booking"], loyalty: ["loyalty"], appointments: ["barber"], request: ["jobs"] };
  const offTabs = Object.entries(WIDGET_TABS).filter(([k]) => widgets[k] === false).flatMap(([, ts]) => ts);
  const loyaltyOnly = widgets.loyalty !== false && Object.keys(WIDGET_TABS).filter((k) => k !== "loyalty").every((k) => widgets[k] === false);
  const planLimited = Object.values(widgets).some((v) => v === false);
  const LOYALTY_ONLY_TABS = ["loyalty", "customers", "marketing", "settings", "help"];
  const ALL_TABS = ["connect", "jobs", "barber", "booking", "board", "completed", "register", "products", "locations", "customers", "loyalty", "reporting", "marketing", "events", "settings", "help"];
  let tabs = (vConfig?.tabs ? vConfig.tabs.filter((t) => ALL_TABS.includes(t)) : ALL_TABS).filter((t) => !offTabs.includes(t) && (loyaltyOnly ? LOYALTY_ONLY_TABS.includes(t) : inVertical(t)) && (hasOrdering || !ORDERING_ONLY.includes(t)) && (t !== "booking" || hasBooking));
  const defaultTab = loyaltyOnly ? "loyalty" : (vConfig?.default_tab && tabs.includes(vConfig.default_tab)) ? vConfig.default_tab : (hasOrdering ? "board" : (tabs.includes("booking") ? "booking" : tabs[0] || "customers"));
  const tabLabel = (t) => (vConfig?.labels && vConfig.labels[t]) || t;
  // Shift-lock (workflow guardrail): a clocked-in cashier is limited to POS tabs; a manager (or a
  // manager-PIN elevation) unlocks everything except owner Settings. Clocking out restores full access.
  // NOTE: this is a convenience lock, not a security boundary — the real boundary is the MorrAccess session.
  const roleLevel = !shift ? "owner" : (shift.role === "manager" || elevated ? "manager" : "cashier");
  const CASHIER_TABS = ["register", "board", "completed", "customers", "booking", "help"];
  const OWNER_ONLY_TABS = ["settings"];
  const tabAllowed = (t) => {
    if (roleLevel === "owner") return true;
    if (roleLevel === "manager") return !OWNER_ONLY_TABS.includes(t);
    return CASHIER_TABS.includes(t);
  };
  const effectiveTab = (() => {
    if (focusView && tabAllowed(focusView)) return focusView;
    const t = activeTab || defaultTab;
    if (tabAllowed(t)) return t;
    return tabAllowed("register") ? "register" : (tabAllowed("board") ? "board" : "customers");
  })();
  const submitLockPin = async () => {
    // Elevate if any active manager PIN matches (owner has no PIN — Settings needs clock-out).
    try {
      const { data: emps } = await opApi.get("/tenant/employees");
      let ok = false;
      for (const e of (emps || []).filter((x) => x.active && x.role === "manager")) {
        try { await opApi.post("/tenant/employees/verify-pin", { employee_id: e.id, pin: lockPin }); ok = true; break; } catch (_) {}
      }
      if (!ok) { toast.error("That PIN isn't a manager PIN."); return; }
      if (OWNER_ONLY_TABS.includes(lockTarget)) { toast.error("Settings is owner-only — clock out to access it."); setLockTarget(null); return; }
      setElevated(true); setActiveTab(lockTarget); setLockTarget(null); setLockPin("");
      toast.success("Manager access unlocked for this shift.");
    } catch (e) { toast.error("Could not verify PIN."); }
  };
  const needsOnboarding = operator.tenant && operator.tenant.onboarded === false;

  const completeOnboarding = async (business_type, selModes, cart_type = "food") => {
    try {
      if (cart_type && cart_type !== "food") await opApi.patch("/tenant/cart-type", { cart_type });
      await opApi.put("/tenant/settings", { business_type, modes: selModes, onboarded: true });
      await refresh();
      await loadAll();
      toast.success("You're all set!");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const advance = async (o, status) => {
    setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, status } : x));
    try {
      await opApi.patch(`/tenant/orders/${o.id}/status`, { status });
      loadOrders(); if (status === "completed") { loadAll(); loadCompleted(completedFrom, completedTo); }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); loadOrders(); }
  };

  const toggleMute = () => { primeAudio(); requestNotifyPermission(); mutedRef.current = !mutedRef.current; setMuted(mutedRef.current); };

  const setStoreFlag = async (field, value) => {
    try { await opApi.put("/tenant/settings", { [field]: value }); await refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStoreState = async (patch) => {
    try { await opApi.put("/tenant/settings", patch); await refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const scopeAvail = (scope) => {
    if (scope === "all" || !scope) return { open: operator.tenant?.open !== false, accepting: operator.tenant?.accepting_orders !== false };
    if (scope === "primary") return { open: operator.tenant?.primary_open !== false, accepting: operator.tenant?.primary_accepting !== false };
    const l = locations.find((x) => x.id === scope) || {};
    return { open: l.open !== false, accepting: l.accepting_orders !== false };
  };
  const setScopeOpen = (scope, makeOpen) => {
    if (scope === "all" || !scope) { setStoreDialog(makeOpen ? "reopen" : "close"); return; }
    if (scope === "primary") { setStoreState({ primary_open: makeOpen, open: makeOpen }); return; }
    opApi.patch(`/tenant/locations/${scope}/open`, { open: makeOpen }).then(loadAll).catch(() => toast.error("Couldn't update location"));
  };
  const setScopePaused = (scope, makeAccepting) => {
    if (scope === "all" || !scope) { if (makeAccepting) { setStoreDialog("resume"); } else { setPauseMode(operator.tenant?.pause_mode || "walkups"); setStoreDialog("pause"); } return; }
    if (scope === "primary") { setStoreState({ primary_accepting: makeAccepting, accepting_orders: makeAccepting }); return; }
    opApi.patch(`/tenant/locations/${scope}/accepting`, { accepting_orders: makeAccepting }).then(loadAll).catch(() => toast.error("Couldn't update location"));
  };
  const setMode = async (id, mode) => {
    try {
      await opApi.patch(`/tenant/locations/${id}/mode`, { mode });
      await Promise.all([loadAll(), refresh()]);
      toast.success(mode === "open" ? "Open now — accepting orders till end of day"
        : mode === "closed" ? "Closed for today — reverts to your hours overnight"
        : "Following your posted hours");
    } catch (e) { toast.error("Couldn't update"); }
  };

  const printTicket = (o) => {
    const w = window.open("", "_blank", "width=340,height=640");
    if (!w) { toast.error("Allow pop-ups to print tickets."); return; }
    const tzOpt = settings?.timezone ? { timeZone: settings.timezone } : {};
    const rows = o.items.map((it) => `<tr><td>${it.qty}× ${it.name}</td><td style="text-align:right">${money(it.price * it.qty)}</td></tr>`).join("");
    const when = o.fulfillment === "pickup_later"
      ? "Pickup later" + (o.scheduled_at ? ": " + new Date(o.scheduled_at).toLocaleString([], tzOpt) : "")
      : "Pickup now";
    w.document.write(`<html><head><title>Ticket #${o.order_number}</title><style>
      *{font-family:'Courier New',monospace;font-size:13px} body{padding:12px;width:300px;margin:0}
      h2{font-size:16px;margin:0 0 4px} table{width:100%;border-collapse:collapse} td{padding:2px 0}
      .hr{border-top:1px dashed #000;margin:8px 0} .tot{font-weight:bold;font-size:15px;display:flex;justify-content:space-between}
      .c{text-align:center}</style></head><body>
      <h2 class="c">${(operator.tenant?.name || "Order").replace(/</g, "")}</h2>
      <div class="c">Ticket #${o.order_number}</div>
      <div class="c">${new Date(o.created_at).toLocaleString([], tzOpt)}</div>
      <div class="hr"></div>
      <div><b>${(o.customer_name || "").replace(/</g, "")}</b> ${(o.customer_phone || "").replace(/</g, "")}</div>
      <div>${when}</div>
      <div class="hr"></div>
      <table>${rows}</table>
      <div class="hr"></div>
      <div class="tot"><span>TOTAL</span><span>${money(o.total)}</span></div>
      ${o.notes ? `<div class="hr"></div><div>Note: ${String(o.notes).replace(/</g, "")}</div>` : ""}
      <div class="hr"></div>
      <div class="c">${o.payment_status === "paid" ? "PAID" : "PAY IN PERSON"}</div>
    </body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  const saveProduct = async () => {
    const images = (prodForm.images || []).filter(Boolean).slice(0, 10);
    const variant_options = (prodForm.variant_options || [])
      .map((o) => ({ name: (o.name || "").trim() || (o.price_driving ? "Size" : "Option"), price_driving: !!o.price_driving,
        values: (o.values || []).map((v) => ({ name: (v.name || "").trim(), ...(o.price_driving ? { price: parseFloat(v.price) || 0 } : {}) })).filter((v) => v.name) }))
      .filter((o) => o.values.length);
    const payload = { ...prodForm, price: parseFloat(prodForm.price) || 0, variant_options, images, image: images[0] || "" };
    try {
      if (editId) await opApi.put(`/tenant/products/${editId}`, payload);
      else await opApi.post("/tenant/products", payload);
      toast.success("Saved"); setProdOpen(false); setProdForm(EMPTY_PROD); setEditId(null);
      const { data } = await opApi.get("/tenant/products"); setProducts(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const loadCatNodes = async () => {
    try {
      const [cn, pr] = await Promise.all([opApi.get("/tenant/product-categories"), opApi.get("/tenant/products")]);
      setCatNodes(cn.data || []); setProducts(pr.data);
    } catch (e) {}
  };
  const loadModGroups = async () => {
    try { const { data } = await opApi.get("/tenant/modifier-groups"); setModGroups(data || []); } catch (e) {}
  };

  const timeAgo = (isoStr) => {
    if (!isoStr) return null;
    const t = new Date(isoStr).getTime();
    if (isNaN(t)) return null;
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };
  const syncCatalog = async () => {
    setSyncing(true);
    try {
      const { data } = await opApi.post("/tenant/catalog/sync");
      toast.success(`Catalog synced · ${data.created} added, ${data.updated} updated${data.archived ? `, ${data.archived} hidden` : ""}`);
      const [p, s] = await Promise.all([opApi.get("/tenant/products"), opApi.get("/tenant/settings")]);
      setProducts(p.data); setSettings(s.data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const addImageUrl = () => {
    const u = imgUrl.trim();
    if (!u) return;
    if ((prodForm.images || []).length >= 10) { toast.error("Up to 10 images"); return; }
    setProdForm((f) => ({ ...f, images: [...(f.images || []), u] }));
    setImgUrl("");
  };
  const removeImage = (i) => setProdForm((f) => ({ ...f, images: (f.images || []).filter((_, idx) => idx !== i) }));
  const makePrimary = (i) => setProdForm((f) => { const arr = [...(f.images || [])]; const [x] = arr.splice(i, 1); arr.unshift(x); return { ...f, images: arr }; });
  const onUploadFiles = async (files) => {
    if (!files || !files.length) return;
    const room = 10 - (prodForm.images || []).length;
    const list = Array.from(files).slice(0, Math.max(0, room));
    if (!list.length) { toast.error("Up to 10 images"); return; }
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await opApi.post("/tenant/upload", fd);
        setProdForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
      }
      toast.success("Uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const editProduct = (p) => {
    const images = (p.images && p.images.length ? p.images : (p.image ? [p.image] : []));
    setEditId(p.id);
    setProdForm({ ...p, price: String(p.price), images });
    setCustomCat(!!p.category && !categories.includes(p.category));
    setProdOpen(true);
  };
  const delProduct = async (p) => {
    try { await opApi.delete(`/tenant/products/${p.id}`); setProducts((x) => x.filter((y) => y.id !== p.id)); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const uploadLogo = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await opApi.post("/tenant/upload", fd);
      setSettings((s) => ({ ...s, logo_url: data.url }));
      toast.success("Logo uploaded — remember to save");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLogoUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const saveSettings = async () => {
    if (!(settings.business_email || "").trim()) { toast.error("Business email is required."); return; }
    if (!(settings.mailing_address || "").trim()) { toast.error("Mailing address is required for email compliance."); return; }
    try {
      const { data } = await opApi.put("/tenant/settings", {
        name: settings.name, slug: settings.slug, primary_color: settings.primary_color,
        brand_menu_cta: settings.brand_menu_cta,
        business_hours: settings.business_hours, cash_enabled: settings.cash_enabled,
        hours: settings.hours, holidays: settings.holidays, logo_url: settings.logo_url,
        cta_label: settings.cta_label, item_noun: settings.item_noun, catalog_heading: settings.catalog_heading,
        popular_label: settings.popular_label, slider_heading: settings.slider_heading, slider_subheading: settings.slider_subheading,
        business_email: settings.business_email, business_phone: settings.business_phone,
        mailing_address: settings.mailing_address,
        timezone: settings.timezone || undefined,
        location_widget: settings.location_widget,
        tax_enabled: settings.tax_enabled, tax_rate: parseFloat(settings.tax_rate) || 0, tax_label: settings.tax_label,
        tax_categories: Object.fromEntries(Object.entries(settings.tax_categories || {}).filter(([, v]) => v !== "" && v != null && !isNaN(parseFloat(v))).map(([k, v]) => [k, parseFloat(v)])),
      });
      setSettings(data); toast.success("Settings updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const sectionSave = (id) => (
    <div className="pt-4 mt-2 border-t border-border">
      <Button size="sm" onClick={saveSettings} className="rounded-none gap-1.5" data-testid={`settings-save-${id}`}>
        <Save className="h-4 w-4" /> Save
      </Button>
    </div>
  );

  const avgMin = ov ? Math.round(ov.avg_prep_seconds / 60) : 0;
  const secToMin = (s) => (s != null ? Math.round(s / 60) + "m" : "—");
  const todayOrders = stats?.today_count ?? "—";
  const hourOrders = stats?.this_hour_count ?? 0;
  const todayAvg = stats ? secToMin(stats.avg_prep_today_seconds) : "—";
  const hourAvg = stats ? secToMin(stats.avg_prep_hour_seconds) : "—";
  const upcomingBookings = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const limit = new Date(today); limit.setDate(limit.getDate() + 7);
    return bookings.filter((b) => {
      if (b.status === "completed" || b.status === "cancelled") return false;
      if (!b.date) return false;
      const d = new Date(b.date + "T00:00:00");
      return d >= today && d <= limit;
    });
  })();

  const completedPanel = (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div>
          <h2 className="font-display font-bold text-lg">Completed tickets</h2>
          <p className="text-xs text-muted-foreground">Finished orders &amp; completed events{completed.timezone ? ` (${completed.timezone})` : ""} · {completed.date || "today"}</p>
        </div>
        <div className="flex items-end gap-2 sm:ml-auto flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">From</label>
            <input type="date" className="h-9 rounded-none border border-input bg-background px-2 text-sm" value={completedFrom} onChange={(e) => setCompletedFrom(e.target.value)} data-testid="completed-date-from" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">To</label>
            <input type="date" className="h-9 rounded-none border border-input bg-background px-2 text-sm" value={completedTo} onChange={(e) => setCompletedTo(e.target.value)} data-testid="completed-date-to" />
          </div>
          <Button variant="outline" className="rounded-none h-9" onClick={() => loadCompleted(completedFrom, completedTo)} data-testid="completed-refresh">Refresh</Button>
          {(completedFrom || completedTo) && (
            <Button variant="ghost" className="rounded-none h-9 text-xs" onClick={() => { setCompletedFrom(""); setCompletedTo(""); }} data-testid="completed-date-clear">Today</Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="border border-border p-3"><div className="text-xs uppercase tracking-wider text-muted-foreground">Tickets</div><div className="font-mono font-bold text-lg" data-testid="completed-count">{completed.count}</div></div>
        <div className="border border-border p-3"><div className="text-xs uppercase tracking-wider text-muted-foreground">Sales</div><div className="font-mono font-bold text-lg">{money(completed.total)}</div></div>
        <div className="border border-border p-3"><div className="text-xs uppercase tracking-wider text-muted-foreground">Collected</div><div className="font-mono font-bold text-lg">{money(completed.collected)}</div></div>
      </div>
      <Input className="rounded-none mb-4" placeholder="Search by order #, customer or item…" value={completedSearch} onChange={(e) => setCompletedSearch(e.target.value)} data-testid="completed-search" />
      {(() => {
        const cq = completedSearch.trim().toLowerCase();
        const list = (completed.orders || []).filter((o) => !cq
          || String(o.order_number).toLowerCase().includes(cq)
          || (o.customer_name || "").toLowerCase().includes(cq)
          || (o.items || []).some((it) => (it.name || "").toLowerCase().includes(cq)));
        if (!list.length) return <div className="text-sm text-muted-foreground text-center py-10 border border-dashed border-border" data-testid="completed-empty">No completed tickets{cq ? " match your search" : " for this day yet"}.</div>;
        return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="completed-list">{list.map((o) => <OrderCard key={o.id} o={o} onAdvance={advance} onPrint={printTicket} onEdit={setEditingOrder} tz={settings?.timezone} />)}</div>;
      })()}
    </>
  );

  return (
    <>
    <OnboardingModal open={!!needsOnboarding && !loyaltyOnly && !focusView && !obDismissed} onComplete={completeOnboarding} onCancel={dismissOnboarding} />
    <LoyaltySetup open={!!needsOnboarding && loyaltyOnly && !focusView} onDone={async () => { await refresh(); await loadAll(); }} />
    <Shell brandName={brandFor(vConfig).name} badge={operator.role === "superadmin" ? "Support view" : "Tenant"} onLogout={logout}
      right={<span className="flex items-center gap-3"><span className="text-sm text-muted-foreground hidden sm:inline">{operator.tenant?.name}</span><MorrMenu /></span>}
      extra={operator.impersonating && (
        <div className="bg-accent text-white px-4 sm:px-6 py-2 text-sm flex items-center justify-between" data-testid="impersonation-banner">
          <span>You are viewing <strong>{operator.tenant?.name}</strong> as platform support.</span>
          <Button size="sm" variant="secondary" className="rounded-none gap-1 h-7" data-testid="stop-impersonate-btn"
            onClick={async () => { await stopImpersonate(); navigate(brandPath("/admin")); }}><ArrowLeft className="h-3.5 w-3.5" /> Back to platform</Button>
        </div>
      )}>
      {needsOnboarding && obDismissed && !focusView && (
        <div className="border border-primary bg-primary/5 px-4 py-3 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="finish-setup-banner">
          <span className="text-sm">Your {vConfig?.brand === "morrconnect" ? "page" : "shop"} setup isn't finished yet. Pick your business type and accept the terms to go live.</span>
          <Button size="sm" className="rounded-none" onClick={reopenOnboarding} data-testid="finish-setup-btn">Finish setup</Button>
        </div>
      )}
      {!needsOnboarding && !focusView && !loyaltyOnly && <GoogleSetupNudge onOpen={() => { setActiveTab("settings"); setSettingsSections((x) => [...new Set([...x, "google"])]); }} />}
      {!focusView && <EmailProblems />}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {settings?.logo_url && <img src={imgSrc(settings.logo_url)} alt="" className="h-10 w-10 sm:h-12 sm:w-12 object-contain border border-border bg-white p-1" data-testid="dashboard-header-logo" />}
        <h1 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl">{operator.tenant?.name || "Store"}</h1>
        {focusView && <span className="ml-2 text-xs font-mono uppercase tracking-widest border border-border px-2 py-1 text-muted-foreground" data-testid="focus-badge">{focusView} · full screen</span>}
        {!focusView && hasOrdering && (() => {
          const tz = settings?.timezone;
          const primDoc = { open: operator.tenant?.primary_open, accepting_orders: operator.tenant?.primary_accepting, type: "fixed", hours: settings?.hours || operator.tenant?.hours || {}, holidays: settings?.holidays || operator.tenant?.holidays || [], open_now_date: operator.tenant?.primary_open_now_date, closed_now_date: operator.tenant?.primary_closed_now_date };
          const mode = _modeOf(primDoc, tz);
          const st = locLiveStatus(primDoc, tz);
          const MODE_LABEL = { auto: "Following hours", open: "Open now", closed: "Closed" };
          const dotCls = mode === "open" ? "bg-green-500" : mode === "closed" ? "bg-red-500" : LOC_DOT[st.tone];
          const overridden = (locations || []).filter((l) => l.active !== false && _modeOf(l, tz) !== "auto").length;
          return (
            <button type="button" onClick={() => setActiveTab("board")}
              className="inline-flex items-center gap-1.5 border border-border rounded-full px-2.5 py-1 text-xs font-semibold hover:bg-muted transition-colors whitespace-nowrap"
              data-testid="store-state-chip" title="Your store's live status — click to manage it on the Board">
              <span className={`h-2 w-2 rounded-full ${dotCls}`} />
              <span className="text-muted-foreground font-normal">Currently:&nbsp;</span>
              <span className={mode === "closed" ? "text-red-600" : mode === "open" ? "text-emerald-600" : "text-foreground"}>{MODE_LABEL[mode]}</span>
              {mode === "auto" && <span className="text-muted-foreground font-normal">&nbsp;·&nbsp;{st.text}</span>}
              {overridden > 0 && <span className="text-muted-foreground font-normal">&nbsp;·&nbsp;{overridden} spot{overridden > 1 ? "s" : ""} changed</span>}
            </button>
          );
        })()}
        {!focusView && hasOrdering && (
          <Button variant="outline" size="sm" className="rounded-none gap-1.5 ml-auto" onClick={() => { window.open("/dashboard?focus=board", "_blank"); window.open("/dashboard?focus=register", "_blank"); }} data-testid="open-stations-btn">
            <ExternalLink className="h-4 w-4" /> Open Register + Board
          </Button>
        )}
      </div>

      {!focusView && vConfig?.brand !== "morrconnect" && vConfig?.flow === "appointment" && !loyaltyOnly && <BookingStats />}
      {!focusView && planLimited && operator.morr_links?.plans && (
        <div className="flex items-center justify-end mb-3 -mt-2" data-testid="plan-add-more">
          <MorrLink to="plans" label="Add more to your plan" testId="plan-add-more-link" />
        </div>
      )}
      {!focusView && vConfig?.brand !== "morrconnect" && vConfig?.flow !== "appointment" && !loyaltyOnly && widgets.menu !== false && (
      <div className="border border-border mb-6 bg-card grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border" data-testid="tenant-stats-card">
        <div className="px-5 py-3.5" data-testid="tenant-stat-active">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Active orders</div>
          <div className="mt-1 font-mono font-bold text-2xl">{ov?.active_orders ?? "—"}</div>
          <div className="text-[10px] text-muted-foreground">in queue now</div>
        </div>
        <div className="px-5 py-3.5" data-testid="tenant-stat-orders">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total orders</div>
          <div className="mt-1 font-mono font-bold text-2xl">{hourOrders}</div>
          <div className="text-[10px] text-muted-foreground">this hour</div>
          <div className="mt-1.5 text-[11px] text-muted-foreground border-t border-border pt-1.5" data-testid="tenant-stat-orders-today">
            <span className="font-mono font-semibold text-foreground">{todayOrders}</span> today
          </div>
        </div>
        <div className="px-5 py-3.5" data-testid="tenant-stat-customers">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Customers</div>
          <div className="mt-1 font-mono font-bold text-2xl">{ov?.customers ?? "—"}</div>
          <div className="text-[10px] text-muted-foreground">all time</div>
        </div>
        <div className="px-5 py-3.5" data-testid="tenant-stat-avg">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Avg ready time</div>
          <div className="mt-1 font-mono font-bold text-2xl text-primary">{hourAvg}</div>
          <div className="text-[10px] text-muted-foreground">this hour</div>
          <div className="mt-1.5 text-[11px] text-muted-foreground border-t border-border pt-1.5" data-testid="tenant-stat-avg-today">
            <span className="font-mono font-semibold text-foreground">{todayAvg}</span> today
          </div>
        </div>
      </div>
      )}

      {shift && (
        <div className="mb-4 flex items-center gap-3 border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="shift-lock-bar">
          <Lock className="h-4 w-4 text-primary" />
          <div className="flex-1 text-sm">
            <span className="font-medium">{shift.name}</span>
            <span className="text-muted-foreground"> on shift · {roleLevel === "manager" ? "Manager access" : "Cashier access (POS mode)"}</span>
          </div>
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => setShift(null)} data-testid="shift-clock-out-btn">Clock out</Button>
        </div>
      )}

      <Tabs value={effectiveTab} onValueChange={(v) => { if (tabAllowed(v)) { setActiveTab(v); } else { setLockTarget(v); setLockPin(""); } }}>
        {!focusView && (
        <TabsList className="rounded-none bg-secondary border border-border h-auto flex-wrap justify-start">
          {tabs.filter((t) => !(t === "completed" && roleLevel !== "cashier")).map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-none capitalize data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`tenant-tab-${t}`}>
              {!tabAllowed(t) && <Lock className="h-3 w-3 mr-1 opacity-60" />}{tabLabel(t)}
            </TabsTrigger>
          ))}
        </TabsList>
        )}

        {hasBooking && (
          <TabsContent value="booking" className="mt-6">
            <div className="border border-dashed border-border p-10 text-center" data-testid="booking-coming-soon">
              <h2 className="font-display font-bold text-xl">Booking module — coming soon</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Services, staff calendars and time-slot booking are on the way. Your business type is saved.</p>
            </div>
          </TabsContent>
        )}

        <TabsContent value="register" className="mt-6">
          <RegisterTab products={products} customers={customers} catNodes={catNodes} modGroups={modGroups}
            slug={settings?.slug || operator.tenant?.slug} accent={settings?.primary_color}
            shift={shift} setShift={setShift} expandHref={focusView ? null : "/dashboard?focus=register"}
            taxConfig={{ enabled: settings?.tax_enabled, rate: settings?.tax_rate, label: settings?.tax_label, categories: settings?.tax_categories || {} }}
            onRefresh={() => { loadOrders(); loadAll(); }} />
        </TabsContent>

        <TabsContent value="connect" className="mt-6">
          <ConnectAdmin />
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <FieldServiceAdmin onCartChanged={(k) => { setCartType(k); loadAll(); }} />
        </TabsContent>

        <TabsContent value="barber" className="mt-6">
          <BarberAdmin onCartChanged={(k) => { setCartType(k); loadAll(); }} />
        </TabsContent>

        <TabsContent value="board" className="mt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs font-mono text-[hsl(var(--success))]" data-testid="board-live-indicator">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--success))] animate-pulse" /> LIVE
              </span>
              {newCount > 0 && (
                <button onClick={() => setNewCount(0)} data-testid="new-orders-badge"
                  className="flex items-center gap-1.5 bg-accent text-white text-xs font-bold px-2.5 py-1 animate-pulse">
                  <BellRing className="h-3.5 w-3.5" /> {newCount} NEW — click to acknowledge
                </button>
              )}
              {newBookingCount > 0 && (
                <button onClick={() => { setNewBookingCount(0); document.querySelector('[data-testid="board-upcoming-events"]')?.scrollIntoView({ behavior: "smooth" }); }} data-testid="new-bookings-badge"
                  className="flex items-center gap-1.5 bg-[#f59e0b] text-white text-xs font-bold px-2.5 py-1 animate-pulse">
                  <CalendarClock className="h-3.5 w-3.5" /> {newBookingCount} NEW EVENT{newBookingCount === 1 ? "" : "S"} — click to view
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Dialog open={!!storeDialog} onOpenChange={(v) => { if (!v) setStoreDialog(null); }}>
                <DialogContent className="rounded-none sm:max-w-md" data-testid="store-status-dialog">
                  {storeDialog === "close" && (<>
                    <DialogHeader>
                      <DialogTitle>Close the store for the day?</DialogTitle>
                      <DialogDescription>Your website hours, the cart and the Location widget will all show <b>Closed</b> and when you reopen ({nextOpenLabel(settings || operator.tenant)}). Online ordering is turned off until you reopen — your Register still works.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" className="rounded-none" onClick={() => setStoreDialog(null)}>Cancel</Button>
                      <Button className="rounded-none !bg-red-600 hover:!bg-red-700 !text-white" onClick={async () => { await setStoreState({ open: false }); setStoreDialog(null); }} data-testid="store-confirm-close">Close for the day</Button>
                    </DialogFooter>
                  </>)}
                  {storeDialog === "reopen" && (<>
                    <DialogHeader>
                      <DialogTitle>Reopen the store?</DialogTitle>
                      <DialogDescription>Your normal business hours will show again everywhere and online ordering resumes right away.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" className="rounded-none" onClick={() => setStoreDialog(null)}>Cancel</Button>
                      <Button className="rounded-none !bg-emerald-600 hover:!bg-emerald-700 !text-white" onClick={async () => { await setStoreState({ open: true }); setStoreDialog(null); }} data-testid="store-confirm-reopen">Reopen store</Button>
                    </DialogFooter>
                  </>)}
                  {storeDialog === "pause" && (<>
                    <DialogHeader>
                      <DialogTitle>Pause online orders?</DialogTitle>
                      <DialogDescription>You stay open, but online ordering is temporarily turned off. Choose what customers see:</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-1">
                      <button type="button" onClick={() => setPauseMode("walkups")} className={`w-full text-left border p-3 rounded-none transition-colors ${pauseMode === "walkups" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`} data-testid="pause-mode-walkups">
                        <div className="font-semibold text-sm">Walk-ups welcome</div>
                        <div className="text-xs text-muted-foreground">Shows “Online orders paused · Walk-ups welcome.” Register still takes in-person orders.</div>
                      </button>
                      <button type="button" onClick={() => setPauseMode("none")} className={`w-full text-left border p-3 rounded-none transition-colors ${pauseMode === "none" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`} data-testid="pause-mode-none">
                        <div className="font-semibold text-sm">Not accepting any orders</div>
                        <div className="text-xs text-muted-foreground">Shows “We’re not taking orders right now. Please check back soon.”</div>
                      </button>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" className="rounded-none" onClick={() => setStoreDialog(null)}>Cancel</Button>
                      <Button className="rounded-none !bg-red-600 hover:!bg-red-700 !text-white" onClick={async () => { await setStoreState({ accepting_orders: false, pause_mode: pauseMode }); setStoreDialog(null); }} data-testid="store-confirm-pause">Pause orders</Button>
                    </DialogFooter>
                  </>)}
                  {storeDialog === "resume" && (<>
                    <DialogHeader>
                      <DialogTitle>Resume online orders?</DialogTitle>
                      <DialogDescription>Customers will be able to place online orders again right away.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" className="rounded-none" onClick={() => setStoreDialog(null)}>Cancel</Button>
                      <Button className="rounded-none !bg-emerald-600 hover:!bg-emerald-700 !text-white" onClick={async () => { await setStoreState({ accepting_orders: true }); setStoreDialog(null); }} data-testid="store-confirm-resume">Resume orders</Button>
                    </DialogFooter>
                  </>)}
                </DialogContent>
              </Dialog>
              <Button variant="outline" size="sm" className="rounded-none gap-1.5" onClick={toggleMute} data-testid="board-mute-toggle">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />} {muted ? "Muted" : "Sound on"}
              </Button>
              {!focusView && (
                <Button variant="outline" size="sm" className="rounded-none gap-1.5" onClick={() => window.open("/dashboard?focus=board", "_blank")} data-testid="board-newtab-toggle">
                  <ExternalLink className="h-4 w-4" /> Full screen
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3" data-testid="board-shortcuts-hint">Shortcuts: <kbd className="font-mono">A</kbd> accept · <kbd className="font-mono">R</kbd> ready · <kbd className="font-mono">C</kbd> complete (oldest order)</p>
          {stats && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-xs border border-border bg-secondary/40 px-3 py-2" data-testid="board-stats">
              <span className="text-muted-foreground">Today: <b className="text-foreground font-mono" data-testid="stat-today">{stats.today_count}</b></span>
              <span className="text-muted-foreground">In queue: <b className="text-foreground font-mono" data-testid="stat-in-queue">{stats.in_queue}</b></span>
              <span className="text-muted-foreground">Avg prep today: <b className="text-foreground font-mono">{stats.avg_prep_today_seconds ? Math.round(stats.avg_prep_today_seconds / 60) + "m" : "—"}</b></span>
              <span className="text-muted-foreground">Peak hour: <b className="text-foreground font-mono">{stats.peak_hour != null ? `${stats.peak_hour}:00` : "—"}</b></span>
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="board-lane-filter">
            {LANE_FILTERS.map(([k, lbl]) => (
              <button key={k} onClick={() => setLaneFilter(k)} data-testid={`lane-filter-${k}`}
                className={`text-xs font-mono uppercase px-2.5 py-1 border ${laneFilter === k ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{lbl}</button>
            ))}
          </div>
          {(() => {
            const multi = entitlements.multi_location && locations.some((l) => l.active !== false && !_mobileEnded(l, settings?.timezone));
            const scope = locFilter;
            const needsSelection = !locFilter;
            const av = scopeAvail(scope);
            const scopeLabel = scope === "all" ? "all locations" : (scope === "primary" ? (operator.tenant?.name || "the main shop") : ((locations.find((x) => x.id === scope) || {}).name || "this location"));
            const isPrimaryScope = scope === "primary" || (scope === "all" && !multi);
            const isConcreteScope = !needsSelection && !(scope === "all" && multi);  // primary or a single named location
            const scopeDoc = isPrimaryScope
              ? { open: operator.tenant?.primary_open, accepting_orders: operator.tenant?.primary_accepting, type: "fixed", hours: settings?.hours || operator.tenant?.hours || {}, holidays: settings?.holidays || operator.tenant?.holidays || [], open_now_date: operator.tenant?.primary_open_now_date, closed_now_date: operator.tenant?.primary_closed_now_date }
              : (scope && scope !== "all" ? (locations.find((x) => x.id === scope) || null) : null);
            const modeTarget = isPrimaryScope ? "primary" : scope;
            const curMode = _modeOf(scopeDoc, settings?.timezone);
            const scopeSt = scopeDoc ? locLiveStatus(scopeDoc, settings?.timezone) : null;
            return (
              <div className="mb-4 border border-border bg-background p-3" data-testid="board-availability-bar">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Store status for</span>
                  <select className="h-8 rounded-none border border-input bg-background px-2 text-sm font-medium" value={locFilter} onChange={(e) => setLocFilter(e.target.value)} data-testid="board-location-filter">
                    <option value="">Select location…</option>
                    <optgroup label="Shops">
                      <option value="primary">{operator.tenant?.name || "Main shop"} (Main)</option>
                      {locations.filter((l) => l.active !== false && !isMobileType(l.type)).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </optgroup>
                    {(() => {
                      const mob = locations.filter((l) => l.active !== false && isMobileType(l.type));
                      const live = mob.filter((l) => locLiveStatus(l, settings?.timezone).tone === "open");
                      const soon = mob.filter((l) => locLiveStatus(l, settings?.timezone).tone === "soon");
                      return (<>
                        {live.length > 0 && <optgroup label="Mobile · live now">{live.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</optgroup>}
                        {soon.length > 0 && <optgroup label="Coming soon">{soon.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</optgroup>}
                      </>);
                    })()}
                  </select>
                </div>
                {needsSelection ? (
                  <div className="text-[11px] text-muted-foreground" data-testid="board-select-hint">
                    Choose a location above to set it to <b>Follow store hours</b>, <b>Open now</b> or <b>Closed</b>.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isConcreteScope ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <ModeToggle mode={curMode} onChange={(m) => setMode(modeTarget, m)} testidPrefix="board-mode" />
                          {scopeSt && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${LOC_TONE[scopeSt.tone]}`} data-testid="board-scope-status"><span className={`h-1.5 w-1.5 rounded-full ${LOC_DOT[scopeSt.tone]}`} />{scopeSt.text}</span>}
                        </div>
                      ) : (
                        <Button size="sm"
                          className={`rounded-none gap-1.5 !text-white ${av.open ? "!bg-emerald-600 hover:!bg-emerald-700" : "!bg-red-600 hover:!bg-red-700"}`}
                          onClick={() => setScopeOpen(scope, !av.open)} data-testid="board-open-toggle"
                          title={`Open/Closed for ${scopeLabel}. "All locations" closes everything.`}>
                          {av.open ? "Open Today" : "Closed for the day"}
                        </Button>
                      )}
                      <Button size="sm"
                        className={`rounded-none gap-1.5 !text-white ${av.accepting ? "!bg-emerald-600 hover:!bg-emerald-700" : "!bg-red-600 hover:!bg-red-700"}`}
                        onClick={() => setScopePaused(scope, !av.accepting)} data-testid="board-accepting-toggle"
                        title={`Pause/resume online orders for ${scopeLabel}. "All locations" pauses everything.`}>
                        {av.accepting ? "Accepting Orders" : "Orders are Paused"}
                      </Button>
                      {multi && <span className="text-[11px] text-muted-foreground">Controlling <b>{scopeLabel}</b>{scope === "all" ? " (every location)" : ""}</span>}
                    </div>
                    {isConcreteScope && (
                      <div className="mt-2 pt-2 border-t border-border text-[11px] text-muted-foreground" data-testid="board-mode-hint">
                        {curMode === "open"
                          ? <span><b className="text-emerald-700">Open now</b> — accepting orders outside your posted hours. Auto-reverts to your hours overnight.</span>
                          : curMode === "closed"
                            ? <span><b className="text-red-700">Closed today</b> — online ordering is blocked even during posted hours. Auto-reverts overnight. For a multi-day or planned closure, add a <b>special closure</b> in the location's hours instead.</span>
                            : <span><b>Follow store hours</b> — opens &amp; closes automatically by your posted hours{scopeSt ? ` (${scopeSt.text})` : ""}. Use <b>Open now</b> to open early / stay late, or <b>Closed</b> to close for the rest of today.</span>}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
          {entitlements.multi_location && locations.some((l) => l.active !== false && !_mobileEnded(l, settings?.timezone)) && (
            <div className="mb-4 border border-border bg-background" data-testid="board-loc-availability">
              <button type="button" onClick={() => setLocPanelOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:bg-secondary/40" data-testid="board-loc-availability-toggle">
                <span>Per-location availability</span>
                {locPanelOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {locPanelOpen && (<>
              <div className="divide-y divide-border border-t border-border">
                {[{ id: "primary", name: operator.tenant?.name || "Main shop", mobile: false, doc: { open: operator.tenant?.primary_open, accepting_orders: operator.tenant?.primary_accepting, type: "fixed", hours: settings?.hours || operator.tenant?.hours || {}, holidays: settings?.holidays || operator.tenant?.holidays || [], open_now_date: operator.tenant?.primary_open_now_date, closed_now_date: operator.tenant?.primary_closed_now_date } },
                  ...locations.filter((l) => l.active !== false && !_mobileEnded(l, settings?.timezone)).map((l) => ({ id: l.id, name: l.name, mobile: isMobileType(l.type), doc: l }))
                ].map((row) => {
                  const st = locLiveStatus(row.doc || {}, settings?.timezone);
                  const rowMode = _modeOf(row.doc || {}, settings?.timezone);
                  return (
                  <div key={row.id} className="flex items-center justify-between px-3 py-2 text-sm gap-2 flex-wrap" data-testid={`board-loc-row-${row.id}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="truncate">{row.name}</span>
                      <span className="text-[10px] font-mono uppercase text-muted-foreground shrink-0">{row.mobile ? "Mobile" : "Shop"}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <ModeToggle mode={rowMode} size="sm" onChange={(m) => setMode(row.id, m)} testidPrefix={`board-loc-mode-${row.id}`} />
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${LOC_TONE[st.tone]}`} data-testid={`board-loc-status-${row.id}`}><span className={`h-1.5 w-1.5 rounded-full ${LOC_DOT[st.tone]}`} />{st.text}</span>
                    </span>
                  </div>
                  );
                })}
              </div>
              <p className="px-3 py-1.5 text-[11px] text-muted-foreground border-t border-border"><b>Auto</b> follows each spot's posted hours. <b>Open now</b> forces it live for the rest of today (open early / stay late); <b>Closed</b> blocks ordering for today even during posted hours. Both revert overnight. For a planned multi-day closure, add a <b>special closure</b> in the location's hours.</p>
              </>)}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {COLUMNS.map((col) => {
              const items = orders.filter((o) => o.status === col.key
                && (laneFilter === "all" || o.lane === laneFilter)
                && (!locFilter || locFilter === "all" || (locFilter === "primary" ? !o.location_id : (o.location_id || "") === locFilter)));
              return (
                <div key={col.key} className={`bg-secondary border border-border border-t-2 ${col.tint} p-3`} data-testid={`kanban-col-${col.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-display font-bold text-sm uppercase tracking-wide">{col.label}</span>
                    <span className="font-mono text-xs bg-background border border-border px-1.5">{items.length}</span>
                  </div>
                  <div className="min-h-[80px]">
                    {items.map((o) => <OrderCard key={o.id} o={o} onAdvance={advance} onPrint={printTicket} onEdit={setEditingOrder} tz={settings?.timezone} />)}
                    {!items.length && <div className="text-xs text-muted-foreground text-center py-6">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {upcomingBookings.length > 0 && (
            <div className="mt-10" data-testid="board-upcoming-events">
              <h2 className="font-display font-bold text-lg mb-1">Upcoming events</h2>
              <p className="text-xs text-muted-foreground mb-3">Next 7 days</p>
              <BookingsList bookings={upcomingBookings} reload={loadOrders} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {completedPanel}
        </TabsContent>

        <TabsContent value="locations" className="mt-6">
          <LocationsTab entitlements={entitlements} locations={locations} reload={loadAll} menus={menus} products={products} modGroups={modGroups} primaryMenuId={settings?.menu_id} tz={settings?.timezone} onGoToSettings={() => { setActiveTab("settings"); setSettingsSections((s) => Array.from(new Set([...(s || []), "location"]))); let tries = 0; const scroll = () => { const el = document.querySelector('[data-testid="settings-section-location"]'); if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); } else if (tries++ < 30) { requestAnimationFrame(scroll); } }; requestAnimationFrame(scroll); }} />
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <Input className="rounded-none flex-1" placeholder="Search products by name or description…" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} data-testid="product-search-input" />
            <select className="h-9 rounded-none border border-input bg-background px-2 text-sm sm:w-52" value={prodCat} onChange={(e) => setProdCat(e.target.value)} data-testid="product-category-filter">
              <option value="all">All categories</option>
              {[...new Set(products.map((p) => p.category).filter(Boolean))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {settings?.morraccess_org_id && (
              <div className="flex items-center gap-2 shrink-0" data-testid="catalog-sync-bar">
                {settings?.catalog_synced_at && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="catalog-synced-chip">Synced {timeAgo(settings.catalog_synced_at)}</span>
                )}
                <Button variant="outline" className="rounded-none gap-2 shrink-0" onClick={syncCatalog} disabled={syncing} data-testid="catalog-sync-btn">
                  <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
                </Button>
              </div>
            )}
            <Button variant="outline" className="rounded-none gap-2 shrink-0" onClick={() => setCatMgrOpen(true)} data-testid="manage-categories-btn"><Plus className="h-4 w-4" /> Manage categories</Button>
            <Button variant="outline" className="rounded-none gap-2 shrink-0" onClick={() => setModMgrOpen(true)} data-testid="manage-modifiers-btn"><Plus className="h-4 w-4" /> Manage modifiers</Button>
            <Dialog open={prodOpen} onOpenChange={(v) => { setProdOpen(v); if (!v) { setEditId(null); setProdForm(EMPTY_PROD); setCustomCat(false); setImgUrl(""); } }}>
              <DialogTrigger asChild><Button className="rounded-none gap-2 shrink-0" data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add product</Button></DialogTrigger>
              <DialogContent className="rounded-none max-h-[92vh] flex flex-col gap-0">
                <DialogHeader className="shrink-0">
                  <DialogTitle className="font-display">{editId ? "Edit" : "New"} product</DialogTitle>
                  <DialogDescription>Set the name, description, price and button label. These drive the wording on your website.</DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto -mx-6 px-6 py-1">
                  <Accordion type="multiple" className="border-y border-border">
                    <AccordionItem value="basics">
                      <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="product-section-basics">Basics</AccordionTrigger>
                      <AccordionContent className="space-y-3 pb-4">
                        <div><Label>Name</Label><Input className="rounded-none mt-1" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} data-testid="product-name-input" /></div>
                        <div><Label>Description</Label><Textarea className="rounded-none mt-1" value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} data-testid="product-desc-input" /></div>
                        <div><Label>Base price <span className="text-muted-foreground normal-case">(used when no sizes are set)</span></Label><Input type="number" step="0.01" className="rounded-none mt-1 max-w-[160px]" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} data-testid="product-price-input" /></div>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="category">
                      <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="product-section-category">Category</AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="border border-border p-3">
                          <CategoryPicker nodes={catNodes} valueId={prodForm.category_id} onChange={(id) => setProdForm({ ...prodForm, category_id: id })} onManage={() => setCatMgrOpen(true)} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="sizes">
                      <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="product-section-sizes">Sizes &amp; options</AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <VariantEditor value={prodForm.variant_options || []} onChange={(variant_options) => setProdForm({ ...prodForm, variant_options })} />
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="modifiers">
                      <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="product-section-modifiers">Modifiers <span className="text-muted-foreground normal-case font-normal lowercase ml-1">(paid add-ons)</span></AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <ModifierAttach groups={modGroups} valueIds={prodForm.modifier_group_ids || []} onChange={(ids) => setProdForm({ ...prodForm, modifier_group_ids: ids })} onManage={() => setModMgrOpen(true)} />
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="images">
                      <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="product-section-images">Images <span className="text-muted-foreground normal-case font-normal ml-1">(up to 10)</span></AccordionTrigger>
                      <AccordionContent className="pb-4">
                        {(prodForm.images || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2" data-testid="product-images-list">
                            {(prodForm.images || []).map((u, i) => (
                              <div key={i} className="relative h-16 w-16 border border-border group" data-testid={`product-image-${i}`}>
                                <img src={imgSrc(u)} alt="" className="h-full w-full object-cover" />
                                {i === 0 && <span className="absolute top-0 left-0 bg-primary text-primary-foreground text-[8px] px-1 font-mono uppercase">Main</span>}
                                <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-black/70 text-white h-4 w-4 flex items-center justify-center text-[10px]" data-testid={`remove-image-${i}`}>×</button>
                                {i !== 0 && <button type="button" onClick={() => makePrimary(i)} className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] py-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`make-primary-${i}`}>Set main</button>}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-1">
                          <Input className="rounded-none" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImageUrl(); } }} placeholder="Paste image URL" data-testid="product-image-url-input" />
                          <Button type="button" variant="outline" className="rounded-none h-9 px-3 text-xs" onClick={addImageUrl} data-testid="add-image-url-btn">Add URL</Button>
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUploadFiles(e.target.files)} data-testid="product-image-file-input" />
                        <Button type="button" variant="outline" className="rounded-none w-full mt-2 gap-2 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-image-btn">
                          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {uploading ? "Uploading…" : "Upload from device"}
                        </Button>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="availability">
                      <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="product-section-availability">Availability &amp; channels</AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <div><Label>Button label override <span className="text-muted-foreground normal-case">(optional — falls back to store default)</span></Label><Input className="rounded-none mt-1" value={prodForm.cta_label} onChange={(e) => setProdForm({ ...prodForm, cta_label: e.target.value })} data-testid="product-cta-input" placeholder="e.g. Pre-order" /></div>
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-2"><Switch checked={prodForm.available} onCheckedChange={(v) => setProdForm({ ...prodForm, available: v })} data-testid="product-available-switch" /><Label>Available</Label></div>
                          <div className="flex items-center gap-2"><Switch checked={prodForm.taxable !== false} onCheckedChange={(v) => setProdForm({ ...prodForm, taxable: v })} data-testid="product-taxable-switch" /><Label>Taxable</Label></div>
                          <div className="flex items-center gap-2"><Switch checked={prodForm.popular === true} onCheckedChange={(v) => setProdForm({ ...prodForm, popular: v })} data-testid="product-popular-switch" /><Label>Most popular</Label></div>
                        </div>
                        <div>
                          <Label>Available in <span className="text-muted-foreground normal-case">(sales channels)</span></Label>
                          <div className="flex flex-wrap gap-4 mt-1.5">
                            {[["online", "Online / Takeout"], ["pos", "POS / Register"], ["events", "Events"]].map(([key, lbl]) => {
                              const on = (prodForm.channels || []).includes(key);
                              return (
                                <label key={key} className="flex items-center gap-1.5 cursor-pointer text-sm" data-testid={`product-channel-${key}`}>
                                  <input type="checkbox" checked={on} onChange={(e) => {
                                    const cur = prodForm.channels || [];
                                    setProdForm({ ...prodForm, channels: e.target.checked ? [...new Set([...cur, key])] : cur.filter((c) => c !== key) });
                                  }} /> {lbl}
                                </label>
                              );
                            })}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Event-only items (Events checked, Online off) stay hidden from your storefront but appear in event packages.</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
                <DialogFooter className="shrink-0 pt-3"><Button onClick={saveProduct} disabled={!prodForm.name} className="rounded-none" data-testid="save-product-btn">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <CategoryTreeManager open={catMgrOpen} onOpenChange={setCatMgrOpen} nodes={catNodes} reload={loadCatNodes} />
          <ModifiersManager open={modMgrOpen} onOpenChange={setModMgrOpen} groups={modGroups} reload={loadModGroups} />
          {(() => {
            const q = prodSearch.trim().toLowerCase();
            const filteredProducts = products.filter((p) =>
              (prodCat === "all" || p.category === prodCat) &&
              (!q || [p.name, p.description].some((f) => (f || "").toLowerCase().includes(q)))
            );
            return (
          <div>
          <div className="text-xs text-muted-foreground mb-2" data-testid="product-count">Showing {filteredProducts.length} of {products.length} product{products.length === 1 ? "" : "s"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {filteredProducts.map((p) => (
              <div key={p.id} className="border-r border-b border-border p-4 flex gap-3" data-testid={`product-${p.id}`}>
                {(() => { const th = (p.images && p.images[0]) || p.image; return th ? <img src={imgSrc(th)} alt={p.name} className="h-16 w-16 object-cover" /> : null; })()}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{p.name}</div>
                    <span className="font-mono text-sm">{money(p.price)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{p.description}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {(() => {
                      const crumbs = [p.group, p.category, p.subcategory].map((x) => (x || "").trim()).filter(Boolean);
                      const uniq = crumbs.filter((c, i) => i === 0 || c.toLowerCase() !== crumbs[i - 1].toLowerCase());
                      return <span className="font-mono text-[10px] uppercase bg-secondary px-1.5 py-0.5" data-testid={`product-cat-${p.id}`}>{uniq.length ? uniq.join(" › ") : (p.category || "Uncategorized")}</span>;
                    })()}
                    {p.popular && <span className="font-mono text-[10px] uppercase bg-primary text-primary-foreground px-1.5 py-0.5 flex items-center gap-0.5" data-testid={`product-popular-badge-${p.id}`}><Star className="h-2.5 w-2.5" />{settings.popular_label || "Popular"}</span>}
                    {!p.available && <span className="font-mono text-[10px] uppercase bg-accent text-white px-1.5 py-0.5">off</span>}
                    <button onClick={() => editProduct(p)} className="ml-auto text-muted-foreground hover:text-foreground" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delProduct(p)} className="text-muted-foreground hover:text-accent" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
            {!products.length && <div className="p-4 text-sm text-muted-foreground border-r border-b border-border">No products yet.</div>}
            {products.length > 0 && !filteredProducts.length && <div className="p-4 text-sm text-muted-foreground border-r border-b border-border" data-testid="product-no-match">No products match your search.</div>}
          </div>
          </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="customers" className="mt-6">
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <Input className="rounded-none max-w-sm flex-1" placeholder="Search by name, email or phone…" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="customer-search-input" />
            <Button className="rounded-none gap-1.5" onClick={() => setAddCustOpen(true)} data-testid="customers-add-btn"><UserPlus className="h-4 w-4" /> Add customer</Button>
            <MorrLink to="contacts" label="Contacts & opt-outs" testId="customers-morr-contacts-link" className="h-10" />
          </div>
          {(() => {
            const q = custSearch.trim().toLowerCase();
            const filtered = q
              ? customers.filter((c) => [c.name, c.email, c.phone].some((f) => (f || "").toLowerCase().includes(q)))
              : customers;
            return (
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-left"><tr className="[&>th]:px-4 [&>th]:py-2 [&>th]:font-medium [&>th]:text-xs [&>th]:uppercase [&>th]:tracking-wider">
                    <th>Name</th><th>Email</th><th>Phone</th><th className="text-center">Loyalty</th><th className="text-center">Emails</th><th className="text-right">Orders</th><th className="text-right">Spent</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((c) => (
                      <tr key={c.id} className="[&>td]:px-4 [&>td]:py-2.5" data-testid={`tenant-customer-${c.id}`}>
                        <td className="font-medium"><div className="flex items-center gap-2"><button className="text-left hover:underline focus:outline-none" onClick={() => setDetailCust(c)} data-testid={`customer-open-${c.id}`}>{c.name}</button>{(settings?.business_email || "").trim().toLowerCase() && (c.email || "").trim().toLowerCase() === (settings.business_email || "").trim().toLowerCase() && <span className="font-mono text-[10px] uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 shrink-0" title="This is your store's notification email — owner alerts appear in this contact's email history." data-testid={`customer-owner-badge-${c.id}`}>Owner</span>}</div></td><td className="text-muted-foreground">{c.email}</td><td className="text-muted-foreground">{c.phone || "—"}</td>
                        <td className="text-center"><CustomerLoyaltyCell customerId={c.id} color={loyStatuses[c.id] || "grey"} onChange={loadAll} /></td>
                        <td className="text-center"><CustomerEmailsCell customerId={c.id} customerName={c.name} /></td>
                        <td className="text-right font-mono">{c.order_count}</td><td className="text-right font-mono">{money(c.total_spent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!filtered.length && <div className="p-4 text-sm text-muted-foreground" data-testid="customers-empty">{customers.length ? "No customers match your search." : "No customers yet."}</div>}
              </div>
            );
          })()}
          <AddCustomerDialog open={addCustOpen} onOpenChange={setAddCustOpen} customers={customers} onCreated={() => loadAll()} />
          <CustomerDetailDialog customer={detailCust} open={!!detailCust} onOpenChange={(o) => !o && setDetailCust(null)} />
        </TabsContent>

        <TabsContent value="loyalty" className="mt-6">
          <LoyaltyManager key={operator.tenant?.onboarded ? "ready" : "setup"} tenant={{ slug: settings?.slug || operator.tenant?.slug, name: settings?.name || operator.tenant?.name, logo_url: settings?.logo_url }} />
        </TabsContent>

        <TabsContent value="marketing" className="mt-6">
          <MarketingTab slug={settings?.slug || operator.tenant?.slug || "your-store"} name={settings?.name || operator.tenant?.name} accent={settings?.primary_color} />
        </TabsContent>

        <TabsContent value="events" className="mt-6">
          <EventsManager />
        </TabsContent>

        <TabsContent value="reporting" className="mt-6">
          <Tabs value={reportSub} onValueChange={setReportSub}>
            <TabsList className="rounded-none bg-secondary border border-border">
              <TabsTrigger value="sales" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="report-sub-sales">Sales</TabsTrigger>
              <TabsTrigger value="completed" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="report-sub-completed">Completed</TabsTrigger>
              <TabsTrigger value="zreport" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="report-sub-zreport">Z-Report</TabsTrigger>
            </TabsList>
            <TabsContent value="sales" className="mt-6"><ReportsTab /></TabsContent>
            <TabsContent value="completed" className="mt-6">{completedPanel}</TabsContent>
            <TabsContent value="zreport" className="mt-6"><ZReportTab businessName={operator.tenant?.name || "Store"} /></TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          {settings && (
            <div className="max-w-3xl">
              <Accordion type="multiple" value={settingsSections} onValueChange={setSettingsSections} className="border border-border">
                {cartControls !== "hidden" && (
                <AccordionItem value="carttype" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-carttype"><span>Cart type{cartControls === "admin" && <AdminOnlyTag />}</span></AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-sm text-muted-foreground mb-3">Pick the industry template that matches your business. Switching is non-destructive — nothing is deleted.</p>
                    <CartTypeChooser onChanged={(k) => { setCartType(k); loadAll(); }} />
                    <CartAddons onChanged={loadAll} />
                  </AccordionContent>
                </AccordionItem>
                )}
                <AccordionItem value="google" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-google">Google &amp; SEO</AccordionTrigger>
                  <AccordionContent className="pb-6"><GoogleSetup /></AccordionContent>
                </AccordionItem>
                <AccordionItem value="team" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-team">Team</AccordionTrigger>
                  <AccordionContent className="pb-6"><TeamSettings /></AccordionContent>
                </AccordionItem>
                <AccordionItem value="info" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-info">Business info</AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-6">
                    <div className="flex flex-wrap items-center gap-3 text-xs bg-secondary p-3" data-testid="settings-info-morr">
                      <span className="flex-1 min-w-[200px]">Name, logo and reply-to email come from your Morr business profile.</span>
                      <MorrLink to="business" label="Edit business details" testId="settings-info-morr-link" />
                      <MorrLink to="plans" label="Plans & billing" testId="settings-plans-morr-link" />
                    </div>
                    <div><Label>Business name</Label><Input className="rounded-none mt-1" value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} data-testid="settings-name-input" /><p className="text-xs text-muted-foreground mt-1">Used to auto-generate your cart's privacy policy &amp; terms.</p></div>
                    <div>
                      <Label>Logo <span className="text-muted-foreground normal-case">(small — shows by your name, on loyalty cards, emails &amp; widget)</span></Label>
                      <div className="flex items-center gap-4 mt-1">
                        {settings.logo_url ? (
                          <img src={imgSrc(settings.logo_url)} alt="logo" className="h-14 w-14 object-contain border border-border bg-white p-1" data-testid="settings-logo-preview" />
                        ) : (
                          <div className="h-14 w-14 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground text-center">No logo</div>
                        )}
                        <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files)} data-testid="settings-logo-input" />
                        <Button type="button" variant="outline" className="rounded-none gap-2" disabled={logoUploading} onClick={() => logoRef.current?.click()} data-testid="settings-logo-upload-btn">
                          {logoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {logoUploading ? "Uploading…" : "Upload logo"}
                        </Button>
                        {settings.logo_url && <Button type="button" variant="ghost" className="rounded-none text-destructive text-xs" onClick={() => setSettings({ ...settings, logo_url: "" })} data-testid="settings-logo-remove-btn">Remove</Button>}
                      </div>
                    </div>
                    <div><Label>Business email <span className="text-destructive">*</span></Label><Input type="email" className="rounded-none mt-1" value={settings.business_email || ""} onChange={(e) => setSettings({ ...settings, business_email: e.target.value })} placeholder="hello@yourshop.com" data-testid="settings-bizemail-input" />{!(settings.business_email || "").trim() ? (<p className="text-xs text-amber-600 mt-1" data-testid="settings-bizemail-warning">⚠ Set this so customer replies to your order &amp; event emails land in your inbox. Until it's set, emails tell customers to {(settings.business_phone || "").trim() ? "call/text your phone" : "get in touch"} instead of replying.</p>) : (<p className="text-xs text-muted-foreground mt-1">Customer replies to all order &amp; event emails go here (used as the Reply-To).</p>)}</div>
                    <div><Label>Business phone <span className="text-muted-foreground normal-case">(optional)</span></Label><Input type="tel" className="rounded-none mt-1" value={settings.business_phone || ""} onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })} placeholder="(415) 555-2671" data-testid="settings-bizphone-input" /></div>
                    <div><Label>Mailing address <span className="text-destructive">*</span></Label><Input className="rounded-none mt-1" value={settings.mailing_address || ""} onChange={(e) => setSettings({ ...settings, mailing_address: e.target.value })} placeholder="123 Main St, City, ST 00000" data-testid="settings-address-input" /><p className="text-xs text-muted-foreground mt-1">Required by email regulations. Appears in the footer of every order email.</p></div>
                    <div><Label>Your shopping cart address</Label>
                      <div className="flex items-center mt-1">
                        <Input className="rounded-none" value={settings.slug} onChange={(e) => setSettings({ ...settings, slug: e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase() })} data-testid="settings-slug-input" />
                        <span className="ml-2 text-sm text-muted-foreground font-mono">.morrcart.com</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">This is your cart's own web address. Share it anywhere — link it on your website, drop it in an email, or post it to your social media so customers can order in one tap. <span className="text-foreground">If you change it later, your old address keeps working automatically</span> — existing embeds and links won't break.</p>
                    </div>
                    {sectionSave("info")}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="tax" className={`px-6 ${hasOrdering ? "" : "hidden"}`}>
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-tax">Tax</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-xs text-muted-foreground mb-3">Add a sales-tax rate applied on top of prices at checkout. Turn it off entirely here, or exempt individual items with the "Taxable" toggle in the Products editor.</p>
                    <div className="flex items-center gap-2 mb-4"><Switch checked={!!settings.tax_enabled} onCheckedChange={(v) => setSettings({ ...settings, tax_enabled: v })} data-testid="settings-tax-switch" /><Label>Charge sales tax</Label></div>
                    {settings.tax_enabled && (
                      <><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                        <div><Label>Default tax rate (%)</Label><Input type="number" step="0.01" min="0" className="rounded-none mt-1" value={settings.tax_rate ?? ""} onChange={(e) => setSettings({ ...settings, tax_rate: e.target.value })} placeholder="8.25" data-testid="settings-tax-rate-input" /></div>
                        <div><Label>Label on receipts</Label><Input className="rounded-none mt-1" value={settings.tax_label || ""} onChange={(e) => setSettings({ ...settings, tax_label: e.target.value })} placeholder="Sales Tax" data-testid="settings-tax-label-input" /></div>
                      </div>
                      {(() => {
                        const cats = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();
                        if (!cats.length) return null;
                        const tc = settings.tax_categories || {};
                        return (
                          <div className="mt-5 max-w-md" data-testid="settings-tax-categories">
                            <Label className="text-xs">Per-category rates <span className="text-muted-foreground normal-case">(optional — leave blank to use the default; enter 0 to exempt)</span></Label>
                            <div className="mt-2 space-y-2">
                              {cats.map((c) => (
                                <div key={c} className="flex items-center gap-3">
                                  <span className="text-sm flex-1 truncate">{c}</span>
                                  <div className="flex items-center gap-1 w-28">
                                    <Input type="number" step="0.01" min="0" className="rounded-none h-9" placeholder={`${parseFloat(settings.tax_rate) || 0}`}
                                      value={tc[c] ?? ""} onChange={(e) => setSettings({ ...settings, tax_categories: { ...tc, [c]: e.target.value } })}
                                      data-testid={`settings-tax-cat-${c}`} />
                                    <span className="text-xs text-muted-foreground">%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                    )}
                    {sectionSave("tax")}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="hours" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-hours">Business hours</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="mb-5">
                      <Label>Time zone</Label>
                      <TimezoneSelect value={settings.timezone || ""} onChange={(tz) => setSettings({ ...settings, timezone: tz })} />
                      {settings.timezone ? (
                        <p className="text-xs text-muted-foreground mt-1">Used for pickup "ready by" times, order timestamps, reports and your Z-report.</p>
                      ) : (
                        <p className="text-xs text-amber-600 mt-1" data-testid="settings-timezone-warning">
                          ⚠ No time zone set — times currently show in UTC. Pick your store's time zone{browserTz ? <> (looks like <button type="button" className="underline font-medium" onClick={() => setSettings({ ...settings, timezone: browserTz })} data-testid="settings-timezone-suggest">{browserTz}</button>)</> : null} so pickup times &amp; reports are correct, then Save.
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">Set open/close times per day and add holiday closures. Customers see today's hours (expandable) on your storefront.</p>
                    <HoursEditor
                      hours={settings.hours}
                      holidays={settings.holidays}
                      onChange={(hours, holidays) => setSettings({ ...settings, hours, holidays })}
                    />
                    {sectionSave("hours")}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="location" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-location">Location &amp; contact widget</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-xs text-muted-foreground mb-4">Embed <code className="font-mono text-[11px]">&lt;div data-morrcart-location&gt;&lt;/div&gt;</code> on your website (footer, contact page, anywhere). Your address, hours, phone/email and map update here — never on the website. Address &amp; hours come from the sections above.</p>
                    <LocationWidgetEditor
                      value={settings.location_widget}
                      tenant={settings}
                      onChange={(location_widget) => setSettings({ ...settings, location_widget })}
                    />
                    {sectionSave("location")}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="branding" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-branding">Branding</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <Label>Brand color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <Input type="color" className="rounded-none h-10 w-20 p-1" value={settings.primary_color} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} data-testid="settings-color-input" />
                      <span className="font-mono text-sm text-muted-foreground uppercase">{settings.primary_color}</span>
                    </div>
                    <div className="mt-3">
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Quick picks</div>
                      <div className="flex flex-wrap gap-2" data-testid="brand-presets">
                        {BRAND_PRESETS.map((p) => (
                          <button key={p.color} type="button" title={p.name}
                            onClick={() => setSettings({ ...settings, primary_color: p.color })}
                            className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${(settings.primary_color || "").toLowerCase() === p.color.toLowerCase() ? "border-foreground ring-2 ring-offset-2 ring-offset-background ring-foreground/30" : "border-border"}`}
                            style={{ backgroundColor: p.color }} data-testid={`brand-preset-${p.color.replace("#", "")}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 max-w-md">One accent color keeps things consistent. It themes the parts MorrCart fully controls — the floating cart button, cart/checkout &amp; account drawers, booking wizard, and your QR codes — which run isolated from your site's CSS so they can't be restyled by it. Your product menu still inherits your website's own styling. Text on buttons auto-switches to light or dark for readability.</p>
                    <div className="mt-3 max-w-md">
                      <BrandPreview color={settings.primary_color} menuCta={settings.brand_menu_cta === true} />
                    </div>
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <Switch checked={settings.brand_menu_cta === true} onCheckedChange={(v) => setSettings({ ...settings, brand_menu_cta: v })} data-testid="settings-brand-cta-switch" className="mt-0.5" />
                      <span className="text-sm">Also brand menu buttons on other websites
                        <span className="block text-xs text-muted-foreground">On your MorrCart page the accent is always used. When you embed the cart on your own website, leave this off to keep your site's own button styling — or turn it on to force your brand color on the "Order / Add" buttons there too.</span>
                      </span>
                    </label>
                    {sectionSave("branding")}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="ordering" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-ordering">Ordering &amp; wording</AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-6">
                    <div className="flex items-center gap-2"><Switch checked={settings.cash_enabled} onCheckedChange={(v) => setSettings({ ...settings, cash_enabled: v })} data-testid="settings-cash-switch" /><Label>Accept cash / pay-in-person orders</Label></div>
                    <div><Label>Button label (CTA)</Label><Input className="rounded-none mt-1" value={settings.cta_label || ""} onChange={(e) => setSettings({ ...settings, cta_label: e.target.value })} data-testid="settings-cta-input" placeholder="Order Now / Book / Add to cart" /><p className="text-xs text-muted-foreground mt-1">Default button text on your products (a barbershop might use "Book").</p></div>
                    <div><Label>Item noun</Label><Input className="rounded-none mt-1" value={settings.item_noun || ""} onChange={(e) => setSettings({ ...settings, item_noun: e.target.value })} data-testid="settings-noun-input" placeholder="coffee / service / pass" /></div>
                    <div><Label>Catalog heading</Label><Input className="rounded-none mt-1" value={settings.catalog_heading || ""} onChange={(e) => setSettings({ ...settings, catalog_heading: e.target.value })} data-testid="settings-heading-input" placeholder="Our Menu / Our Services" /></div>
                    {sectionSave("ordering")}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="slider" className="px-6">
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-slider">Popular &amp; featured slider</AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-6">
                    <div>
                      <Label>"Popular" tag wording</Label>
                      <select className="mt-1 h-9 w-full rounded-none border border-input bg-background px-2 text-sm" value={settings.popular_label || "Popular"} onChange={(e) => setSettings({ ...settings, popular_label: e.target.value })} data-testid="settings-popular-label">
                        {["Popular", "Signature", "Featured", "Bestseller"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">Badge shown on products you mark "Most popular" (in your menu and dashboard).</p>
                    </div>
                    <div><Label>Slider heading</Label><Input className="rounded-none mt-1" value={settings.slider_heading || ""} onChange={(e) => setSettings({ ...settings, slider_heading: e.target.value })} data-testid="settings-slider-heading" placeholder="Our Signature Drinks" /></div>
                    <div><Label>Slider subheading</Label><Input className="rounded-none mt-1" value={settings.slider_subheading || ""} onChange={(e) => setSettings({ ...settings, slider_subheading: e.target.value })} data-testid="settings-slider-subheading" placeholder="Handcrafted with premium ingredients" /></div>
                    <div>
                      <Label>Slider embed code</Label>
                      <p className="text-xs text-muted-foreground mt-1 mb-1.5">Paste this where you want the popular-products slider — on your website or your MorrCart page. It shows every product tagged "Most popular"; clicking a slide opens its full product card.</p>
                      <pre className="bg-secondary text-xs p-3 rounded-none overflow-x-auto whitespace-pre-wrap break-all" data-testid="slider-embed-code">{`<div data-morrcart-featured></div>\n<script src="${BACKEND}/embed.js" data-tenant="${settings?.slug || operator.tenant?.slug || "your-store"}"></script>`}</pre>
                      <Button size="sm" variant="outline" className="rounded-none mt-2 gap-1.5" data-testid="slider-embed-copy" onClick={() => { navigator.clipboard.writeText(`<div data-morrcart-featured></div>\n<script src="${BACKEND}/embed.js" data-tenant="${settings?.slug || operator.tenant?.slug || "your-store"}"></script>`); toast.success("Slider code copied"); }}>Copy code</Button>
                    </div>
                    {sectionSave("slider")}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="staff" className={`px-6 ${hasOrdering ? "" : "hidden"}`}>
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-staff">Staff &amp; PINs</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <StaffManager />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="zreport" className={`px-6 ${hasOrdering ? "" : "hidden"}`}>
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-zreport">End-of-day report (Z-report)</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <ZReport />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="promos" className={`px-6 ${hasOrdering ? "" : "hidden"}`}>
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-promos">Promo codes</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <PromoManager products={products} />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="preview" className={`px-6 ${hasOrdering ? "" : "hidden"}`}>
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-preview">Live preview</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-sm text-muted-foreground mb-4">See exactly how your storefront, cart and customer portal look and behave — before you paste the snippet anywhere.</p>
                    <CartPreviewPanel slug={settings?.slug || operator.tenant?.slug || "your-store"} />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="install" className={`px-6 border-b-0 ${hasOrdering ? "" : "hidden"}`}>
                  <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-install">Add to your website</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-sm text-muted-foreground mb-4">Your cart &amp; customer portal, live on any site — without changing its design.</p>
                    <InstallSnippet slug={settings?.slug || operator.tenant?.slug || "your-store"} />
                    <div className="mt-8 border-t border-border pt-6">
                      <h3 className="font-display font-bold text-base">Test my install</h3>
                      <p className="text-sm text-muted-foreground mt-1 mb-4">Paste your live website URL to verify MorrCart is wired up correctly end-to-end.</p>
                      <EmbedHealth endpoint="/tenant/embed-health" />
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {vConfig?.brand === "morrconnect" && (
                  <AccordionItem value="connect-install" className="px-6 border-b-0">
                    <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="settings-section-connect-install">Add to your website</AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <ConnectInstall slug={settings?.slug || operator.tenant?.slug || "your-business"} />
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>

              <Button onClick={saveSettings} className="rounded-none mt-4" data-testid="save-settings-btn">Save settings</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="help" className="mt-6">
          <HelpCenter />
        </TabsContent>
      </Tabs>

      {editingOrder && (
        <EditOrderDialog order={editingOrder} products={products} modGroups={modGroups} editorName={shift?.name || ""}
          onClose={() => setEditingOrder(null)} onSaved={loadOrders} />
      )}

      <Dialog open={!!lockTarget} onOpenChange={(o) => !o && setLockTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="lock-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Manager access</DialogTitle>
            <DialogDescription>
              {OWNER_ONLY_TABS.includes(lockTarget)
                ? "Settings is owner-only. Clock out to return to full owner access."
                : `A manager PIN is needed to open "${lockTarget}".`}
            </DialogDescription>
          </DialogHeader>
          {!OWNER_ONLY_TABS.includes(lockTarget) && (
            <div className="space-y-3">
              <Input autoFocus type="password" inputMode="numeric" maxLength={6}
                className="rounded-none text-center text-lg tracking-[0.5em]" value={lockPin}
                onChange={(e) => setLockPin(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && submitLockPin()} data-testid="lock-pin-input" />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-none" onClick={() => setLockTarget(null)}>Cancel</Button>
                <Button className="rounded-none" onClick={submitLockPin} data-testid="lock-pin-submit">Unlock</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
    </>
  );
}
